import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Tooltip,
  Alert,
  IconButton,
  MenuItem
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { areConnectorsCompatible, compatibilityMatrix } from '../services/compatibilityMatrix';
import { getCompatibleWireGauges } from '../services/compatibilitySystemV2';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ConnectorImageZoom from './ConnectorImageZoom';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import html2canvas from 'html2canvas';
import { buildConnectorFamilies, getFamilyDescription } from '../services/connectorFamiliesService';

// Import image capture service
import { cableImageCaptureService } from '../services/cableImageCaptureService';
import clientImageCaptureService from '../services/clientImageCaptureService';
import { generateSKU } from '../services/skuService';
import { API_ENDPOINTS } from '../config';
import { imageUrlToBase64DataUri } from '../services/utilityService';

import { CompatibilityWarning } from './BaseConnectorSelector';
import ConnectorCompatibilityIndicator from './ConnectorCompatibilityIndicator';
import { getConnectorCompatibilityWarningSync } from '../services/compatibilitySystemV2';
import { getAvailableWireTypes, getDefaultWireType, wireTypes } from '../services/wireCompatibilityService';
import { formatGenderWithXlrHint } from '../utils/connectorDisplayUtils';


const connectorPoints = [
  { id: 'G', label: 'G', type: 'hybrid' },
  { id: 'H', label: 'H', type: 'hybrid' },
  { id: 'I', label: 'I', type: 'power' },
  { id: 'J', label: 'J', type: 'power' }
];

const lengthLabels = [
  { id: 'A', label: 'A (Overall)' },
  { id: 'B', label: 'B (split to split)' },
  { id: 'C', label: 'C (split to F)' },
  { id: 'D', label: 'D (split to G)' },
  { id: 'E', label: 'E (split to H)' },
  { id: 'F', label: 'F (split to I)' }
];

const PX_PER_INCH = 6; // Unified scaling for all segments

// Set default values for lengths
const defaultLengths = {
  A: 48,  // Overall length
  B: 36,  // split to split - main trunk (calculated)
  C: 6,   // split to F
  D: 6,   // split to G
  E: 6,   // split to H
  F: 6    // split to I
};

// Define pairs at the top-level scope so it is available everywhere
const pairs = { 'G': 'H', 'H': 'G', 'I': 'J', 'J': 'I' };

// Get the appropriate image for connectors based on configuration
function getConnectorImage(connector, configuration) {
  // Get the connector data from the compatibility matrix to ensure we have the correct image
  const connectorData = compatibilityMatrix[connector?.id];
  if (!connectorData?.image) {
    return '/images/coming_soon.png'; // Fallback for missing images
  }
  
  // Return the image from the compatibility matrix
  return connectorData.image;
}

// Helper function to map configuration to mount type
const mapConfigurationToMountType = (configuration) => {
  switch (configuration) {
    case 'standard':
      return 'cable';
    case 'panel-mount':
      return 'panel';
    case 'right-angle':
      return 'rightangle'; // Note: some connectors use 'right-angle', others use 'rightangle'
    default:
      return configuration;
  }
};

// Helper component for connector selection modal - defined at module level
const ConnectorModal = React.memo(function ConnectorModal({ selectedPoint, onConnectorSelect, filteredConnectors, type }) {
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [families, setFamilies] = useState([]);
  
  const point = connectorPoints.find(p => p.id === selectedPoint);

  useEffect(() => {
    // Only process if we have filtered connectors
    if (!filteredConnectors || filteredConnectors.length === 0) {
      return;
    }
    
    // Check if the connectors are actually different from what we already have
    const currentConnectorIds = families.flatMap(f => f.variants.map(v => v.id)).sort();
    const newConnectorIds = filteredConnectors.map(c => c.id).sort();
    
    if (JSON.stringify(currentConnectorIds) === JSON.stringify(newConnectorIds)) {
      return;
    }
    
    // Group connectors back into families for display
    const familyGroups = {};
    filteredConnectors.forEach(connector => {
      const familyKey = connector.family || connector.type;
      if (!familyGroups[familyKey]) {
        familyGroups[familyKey] = {
          id: familyKey.toLowerCase().replace(/\s+/g, '_'),
          name: connector.type || familyKey,
          description: getFamilyDescription(familyKey, point?.type === 'hybrid' ? 'audio' : 'power'),
          image: connector.image,
          variants: []
        };
      }
      familyGroups[familyKey].variants.push(connector);
    });
    
    const familiesArray = Object.values(familyGroups).sort((a, b) => a.name.localeCompare(b.name));
    setFamilies(familiesArray);
  }, [selectedPoint, filteredConnectors, families]);

  // Handle family selection
  const handleFamilySelect = (family) => {
    setSelectedFamily(family.id);
  };

  // Handle connector selection
  const handleConnectorSelect = (connector) => {
    onConnectorSelect(connector);
  };

  // Go back to family selection
  const handleBackToFamilies = () => {
    setSelectedFamily(null);
  };

  return (
    <Box sx={{ maxHeight: '50vh', overflowY: 'auto', mt: 1 }}>
      {!selectedFamily ? (
        // Step 1: Show connector families
        <>
          <Typography variant="body2" color="text.secondary" paragraph>
            Choose the type of connector you need:
          </Typography>
          <Grid container spacing={2}>
            {families.length > 0 ? (
              families.map((family) => (
                <Grid item xs={12} sm={6} key={family.id}>
                  <Paper
                    sx={{
                      cursor: 'pointer',
                      p: 2,
                      height: '100%',
                      '&:hover': {
                        boxShadow: 4,
                        transform: 'translateY(-2px)'
                      },
                      transition: 'all 0.2s ease-in-out'
                    }}
                    onClick={() => handleFamilySelect(family)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ 
                        width: { xs: 36, sm: 48 }, 
                        height: { xs: 36, sm: 48 }, 
                        borderRadius: 1, 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        overflow: 'hidden',
                        flexShrink: 0,
                        '& img': { width: '100%', height: '100%', objectFit: 'contain' }
                      }}>
                        <ConnectorImageZoom 
                          src={family.image || '/images/coming_soon.png'} 
                          alt={family.name}
                          style={{ width: '100%', height: '100%' }}
                        />
                      </Box>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" gutterBottom>
                          {family.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {family.description}
                        </Typography>
                        <Typography variant="caption" color="primary" sx={{ mt: 1, display: 'block' }}>
                          {family.variants.length} options available
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              ))
            ) : (
              <Grid item xs={12}>
                <Alert severity="warning">
                  <Typography variant="body2">
                    No connectors found for type: <strong>{point?.type === 'hybrid' ? 'audio' : 'power'}</strong>. Please check the compatibility matrix configuration.
                  </Typography>
                </Alert>
              </Grid>
            )}
          </Grid>
        </>
      ) : (
        // Step 2: Show connectors within the selected family
        <>
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography 
              variant="body2" 
              color="primary" 
              sx={{ cursor: 'pointer', textDecoration: 'underline' }}
              onClick={handleBackToFamilies}
            >
              ← Back to connector types
            </Typography>
            <Typography variant="body1">
              <strong>{families.find(f => f.id === selectedFamily)?.name}</strong> - {families.find(f => f.id === selectedFamily)?.description}
            </Typography>
          </Box>
          
          <Grid container spacing={2}>
            {families.find(f => f.id === selectedFamily)?.variants.map((connector) => (
              <Grid item xs={12} key={connector.id}>
                <Paper
                  sx={{ 
                    cursor: 'pointer', 
                    '&:hover': { 
                      boxShadow: 6,
                      transform: 'translateY(-2px)'
                    },
                    transition: 'all 0.2s ease-in-out'
                  }}
                  onClick={() => handleConnectorSelect(connector)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
                    <Box sx={{ width: { xs: 36, sm: 48 }, height: { xs: 36, sm: 48 }, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', '& img': { width: '100%', height: '100%', objectFit: 'contain' } }}>
                      <ConnectorImageZoom src={connector.image} alt={connector.name} style={{ width: '100%', height: '100%' }} />
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle1">
                        {connector.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatGenderWithXlrHint(connector)} • {connector.mountType === 'cable' ? 'Cable Mount' : connector.mountType === 'panel' ? 'Panel Mount' : connector.mountType}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </Box>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function
  if (prevProps.selectedPoint !== nextProps.selectedPoint) return false;
  if (prevProps.type !== nextProps.type) return false;
  
  // Compare filtered connectors by content, not reference
  if (prevProps.filteredConnectors.length !== nextProps.filteredConnectors.length) return false;
  
  const prevIds = prevProps.filteredConnectors.map(c => c.id).sort();
  const nextIds = nextProps.filteredConnectors.map(c => c.id).sort();
  
  if (JSON.stringify(prevIds) !== JSON.stringify(nextIds)) return false;
  
  // If we get here, the props are effectively the same
  return true; // true means "don't re-render"
});

function HybridConnectorSelection({ value = {}, onChange, onNext, onBack, onReady, onMissingRequirements, type, svgRef = null, onAddToCart, onImageCaptureReady }) {
  // Feature flag for color choice - can be controlled via props or environment
  const offerColorChoice = value.offerColorChoice ?? process.env.REACT_APP_OFFER_COLOR_CHOICE === 'true' ?? false;
  
  // State for showing tooltip notifications
  const [tooltipMessage, setTooltipMessage] = useState('');
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [tooltipAnchor, setTooltipAnchor] = useState(null);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [filteredConnectors, setFilteredConnectors] = useState([]);
  const [connectors, setConnectors] = useState(() => {
    return value.connectors || value.hybridConnectors?.connectors || {};
  });

  // Internal SVG ref for wheel event handling
  const localSvgRef = useRef(null);
  const actualSvgRef = svgRef || localSvgRef;

  // Helper function to convert relative paths to absolute URLs for client-side rendering
  const getImageUrlForDisplay = useCallback((imagePath) => {
    if (!imagePath) return imagePath;
    if (imagePath.startsWith('data:') || imagePath.startsWith('http')) {
      return imagePath;
    }
    // Convert relative URL to absolute URL for client-side rendering
    return imagePath.startsWith('/') 
      ? `${window.location.origin}${imagePath}`
      : `${window.location.origin}/${imagePath}`;
  }, []);

  // Memoize connector images to prevent repeated getConnectorImage calls
  const connectorImages = useMemo(() => {
    const images = {};
    ['G', 'H', 'I', 'J'].forEach(point => {
      const connector = connectors[point];
      if (connector?.id) {
        const imageUrl = getConnectorImage(connector, connector.configuration || 'standard');
        // Store relative paths directly for consistent Puppeteer capture
        images[point] = imageUrl;
      }
    });
    return images;
  }, [connectors]);
  const [lengths, setLengths] = useState(() => ({
    ...defaultLengths,
    ...(value.lengths || {})
  }));
  const [specialInstructions, setSpecialInstructions] = useState(value.specialInstructions || '');
  const [unit, setUnit] = useState('in');
  const [overallInput, setOverallInput] = useState(unit === 'ft' ? (value.lengths?.A / 12).toFixed(2).replace(/\.00$/, '') : value.lengths?.A || '');
  // Note: touchedLengths is kept for backward compatibility but editedFields is used for validation
  const [touchedLengths, setTouchedLengths] = useState({});
  const [lastManualEdit, setLastManualEdit] = useState(null); // Track which field was last manually edited
  
  // Wire gauge state - always 14AWG for hybrid cables
  const [powerWireGauge, setPowerWireGauge] = useState('14AWG');
  const [availableWireGauges, setAvailableWireGauges] = useState([
    { id: '12AWG', label: '12 AWG', description: 'Heavy duty power applications', ampacity: 20 },
    { id: '14AWG', label: '14 AWG', description: 'Standard power applications', ampacity: 15 },
    { id: '16AWG', label: '16 AWG', description: 'Light power applications', ampacity: 13 }
  ]);
  
  // Wire type state for audio connectors - always PSM for hybrid cables
  const [audioWireType, setAudioWireType] = useState('PSH');
  const [availableAudioWireTypes, setAvailableAudioWireTypes] = useState([]);
  
  // Cable color state - default to black, or use value if color choice is offered
  const [cableColor, setCableColor] = useState(offerColorChoice ? (value.cableColor || 'black') : 'black');
  
  // SVG pan and zoom state
  const [svgTransform, setSvgTransform] = useState({ x: 0, y: 0, scale: 1 });
  const [isPanning, setIsPanning] = useState(false);
  const [lastPanPoint, setLastPanPoint] = useState({ x: 0, y: 0 });

  // Enhanced compatibility checking
  const [compatibilityWarnings, setCompatibilityWarnings] = useState({});
  
  // IEC + 12 AWG compatibility error state
  const [iec12AWGError, setIec12AWGError] = useState(false);
  
  // Ref to track loaded connectors for each point to prevent unnecessary re-loading
  const loadedConnectorsRef = useRef({});

  // Add editedFields state for consistent length validation tracking
  const [editedFields, setEditedFields] = useState(new Set());

  // REMOVED: currentConfig state - not needed for working components
  
  // Reset to default state only when value prop is completely undefined (when switching cable types)
  useEffect(() => {
    if (value === undefined) {
      setConnectors({});
      setLengths(defaultLengths);
      setSpecialInstructions('');
      setPowerWireGauge('14AWG');
      setAudioWireType('PSH');
      setCableColor('black'); // Always reset to black
      setEditedFields(new Set());
      setLastManualEdit(null);
      setOverallInput(String(defaultLengths.A));
      setCompatibilityWarnings({});
      setAvailableAudioWireTypes([]);
      setIec12AWGError(false);
      // REMOVED: currentConfig reset - not needed
    }
  }, [value === undefined]);

  // Update state when value prop changes (for data persistence)
  useEffect(() => {
    if (value) {
      if (value.connectors) {
        setConnectors(value.connectors);
      }
      if (value.lengths) {
        setLengths(prev => ({ ...prev, ...value.lengths }));
      }
      if (value.specialInstructions !== undefined) {
        setSpecialInstructions(value.specialInstructions);
      }
      if (value.powerWireGauge) {
        setPowerWireGauge(value.powerWireGauge);
      }
      if (value.audioWireType) {
        setAudioWireType(value.audioWireType);
      }
      if (offerColorChoice && value.cableColor) {
        setCableColor(value.cableColor);
      } else {
        setCableColor('black'); // Force black when color choice is not offered
      }
    }
  }, [value]);
  
  // Stable onChange wrapper to prevent unnecessary re-renders
  const stableOnChange = useCallback((data) => {
    if (onChange) {
      onChange(data);
    }
  }, [onChange]);

  // Helper function to find the correct connector variant based on configuration
  const findConnectorVariant = useCallback((currentConnector, configuration) => {
    if (!currentConnector || !currentConnector.id) return currentConnector;
    
    // Get the current connector's data from the compatibility matrix
    const currentConnectorData = compatibilityMatrix[currentConnector.id];
    if (!currentConnectorData) {
      return {
        ...currentConnector,
        configuration: configuration
      };
    }
    
    const { family, type, purpose, poles } = currentConnectorData;
    const targetMountType = mapConfigurationToMountType(configuration);
    
    // Search through the compatibility matrix to find the variant with the same family, type, purpose, and poles
    // but with the desired mount type (configuration)
    for (const [connectorId, connectorData] of Object.entries(compatibilityMatrix)) {
      // Handle multi-purpose parsing for compatibility
      const connectorPurposes = connectorData.purpose ? 
        connectorData.purpose.split(',').map(p => p.trim().toLowerCase()) : 
        [];
      const targetPurposes = purpose ? 
        purpose.split(',').map(p => p.trim().toLowerCase()) : 
        [];
      
      if (connectorData.family === family && 
          connectorData.type === type &&
          connectorData.poles === poles &&
          connectorPurposes.some(p => targetPurposes.includes(p)) &&
          (connectorData.mountType === targetMountType || 
           (targetMountType === 'rightangle' && connectorData.mountType === 'right-angle'))) {
        
        // Return the complete connector object with the new ID and all updated properties
        const updatedConnector = {
          ...currentConnector,
          id: connectorId,
          skuCode: connectorData.skuCode || connectorId, // Use skuCode from matrix, fallback to id
          configuration: configuration,
          image: connectorData.image || currentConnector.image, // Use new image or fallback to current
          description: connectorData.description || connectorData.name || currentConnector.description,
          gender: connectorData.gender || currentConnector.gender,
          mountType: connectorData.mountType,
          family: connectorData.family || currentConnector.family,
          type: connectorData.type || currentConnector.type,
          purpose: connectorData.purpose || currentConnector.purpose,
          label: connectorData.name || connectorData.description || currentConnector.label
        };
        
        return updatedConnector;
      }
    }
    
    // If no variant found, return the original connector with updated configuration
    return {
      ...currentConnector,
      configuration: configuration
    };
  }, []); // Empty dependency array since it doesn't depend on any state or props

  // Update available audio wire types when audio connectors change
  useEffect(() => {
    if (connectors.G?.id && connectors.H?.id) {
      // Get wire types compatible with both audio connectors
      const gWires = getAvailableWireTypes(connectors.G.id);
      const hWires = getAvailableWireTypes(connectors.H.id);
      
      // Find intersection of compatible wire types
      const gWireIds = gWires.map(wire => wire.id);
      const hWireIds = hWires.map(wire => wire.id);
      const commonWireIds = gWireIds.filter(id => hWireIds.includes(id));
      
      const commonWires = commonWireIds.map(id => wireTypes[id]).filter(Boolean);
      setAvailableAudioWireTypes(commonWires);
      
      // Set default wire type if current selection is not available
      if (!commonWireIds.includes(audioWireType)) {
        const defaultWire = getDefaultWireType(connectors.G.id);
        if (defaultWire && commonWireIds.includes(defaultWire)) {
          setAudioWireType(defaultWire);
        } else if (commonWires.length > 0) {
          setAudioWireType(commonWires[0].id);
        }
      }
    }
  }, [connectors.G?.id, connectors.H?.id, audioWireType]);

  // Check compatibility between connectors of the same type only
  useEffect(() => {
    const checkRelevantCompatibility = async () => {
      const warnings = {};
      
      // For Hybrid Audio+Power cables, connectors are paired by type:
      // G ↔ H (audio pair)
      // I ↔ J (power pair)
      const pairs = [
        ['G', 'H'], // Audio pair
        ['I', 'J']  // Power pair
      ];
      
      for (const [connA_id, connB_id] of pairs) {
        const connA = connectors[connA_id];
        const connB = connectors[connB_id];
        
        if (connA?.id && connB?.id) {
          try {
            const compatibilityResult = areConnectorsCompatible(connA.id, connB.id);
            if (compatibilityResult && !compatibilityResult.compatible) {
              const key = `${connA_id}-${connB_id}`;
              warnings[key] = {
                type: 'error',
                message: compatibilityResult.reason || 'Incompatible connectors',
                details: compatibilityResult.details
              };
            } else if (compatibilityResult && compatibilityResult.warning) {
              const key = `${connA_id}-${connB_id}`;
              warnings[key] = {
                type: 'warning',
                message: compatibilityResult.warning,
                details: compatibilityResult.notes
              };
            }
            
            // Check wire gauge compatibility if both connectors are power connectors
            if (connA.purpose === 'power' && connB.purpose === 'power') {
              try {
                const wireGaugeWarning = getConnectorCompatibilityWarningSync(connA.id, connB.id);
                if (wireGaugeWarning) {
                  const key = `${connA_id}-${connB_id}`;
                  warnings[key] = {
                    type: 'warning',
                    message: wireGaugeWarning.message || 'Compatible with wire gauge considerations',
                    details: wireGaugeWarning.details || wireGaugeWarning.message
                  };
                }
              } catch (wireGaugeError) {
                console.warn('Error checking wire gauge compatibility:', wireGaugeError);
              }
            }
          } catch (error) {
            console.warn('Error checking compatibility:', error);
          }
        }
      }
      
      setCompatibilityWarnings(warnings);
    };
    
    checkRelevantCompatibility();
  }, [connectors.G?.id, connectors.H?.id, connectors.I?.id, connectors.J?.id, powerWireGauge]);

  // Load available wire gauges when power connectors change
  useEffect(() => {
    const loadWireGauges = async () => {
      const iConnector = connectors['I'];
      const jConnector = connectors['J'];
      
      if (iConnector?.id || jConnector?.id) {
        // Use the first available power connector to get wire gauge options
        const powerConnectorId = iConnector?.id || jConnector?.id;
        
        try {
          const gaugeStrings = await getCompatibleWireGauges(powerConnectorId, 'power');
          
          if (gaugeStrings && gaugeStrings.length > 0) {
            // Convert string array to object array with proper format
            const wireGaugeMapping = {
              '12AWG': { id: '12AWG', label: '12 AWG', description: 'Heavy duty power applications', ampacity: 20 },
              '14AWG': { id: '14AWG', label: '14 AWG', description: 'Standard power applications', ampacity: 15 },
              '16AWG': { id: '16AWG', label: '16 AWG', description: 'Light power applications', ampacity: 13 }
            };
            
            const convertedGauges = gaugeStrings
              .map(gaugeString => wireGaugeMapping[gaugeString])
              .filter(Boolean); // Remove any undefined entries
            
            setAvailableWireGauges(convertedGauges);
            
            // Check if current powerWireGauge is still compatible
            const currentGaugeId = powerWireGauge;
            const isCurrentGaugeCompatible = convertedGauges.some(gauge => gauge.id === currentGaugeId);
            
            if (!isCurrentGaugeCompatible && convertedGauges.length > 0) {
              // Current gauge is not compatible, switch to the first available compatible gauge
              const newGauge = convertedGauges[0].id;
              setPowerWireGauge(newGauge);
              stableOnChange({ connectors, lengths, specialInstructions, powerWireGauge: newGauge, audioWireType, cableColor });
            }
          }
          // If no gauges returned, keep the default ones we initialized with
        } catch (error) {
          console.error('Error loading wire gauges:', error);
          // Keep the default wire gauges we initialized with
        }
      }
      // If no power connectors selected, keep the default wire gauges
    };
    
    loadWireGauges();
  }, [connectors.I?.id, connectors.J?.id, powerWireGauge, onChange, lengths, specialInstructions, audioWireType]);

  // Check for IEC + 12 AWG incompatibility
  useEffect(() => {
    const hasIECConnector = Object.values(connectors).some(connector => 
      isIECConnector(connector)
    );
    const is12AWG = powerWireGauge === '12AWG';
    
    const hasIncompatibility = hasIECConnector && is12AWG;
    setIec12AWGError(hasIncompatibility);
    
    if (hasIncompatibility) {
      console.warn('⚠️ IEC connector detected with 12 AWG wire gauge - incompatibility!');
    }
  }, [connectors.G?.id, connectors.H?.id, connectors.I?.id, connectors.J?.id, powerWireGauge]);

  // Power wire gauge is always 14AWG for hybrid cables - ignore prop changes
  useEffect(() => {
    // Always maintain 14AWG for hybrid cables, ignore any external changes
    if (powerWireGauge !== '14AWG') {
      setPowerWireGauge('14AWG');
    }
  }, [powerWireGauge]);

  // Audio wire type is always PSH for hybrid cables - ignore prop changes
  useEffect(() => {
    // Always maintain PSH for hybrid cables, ignore any external changes
    if (audioWireType !== 'PSH') {
      setAudioWireType('PSH');
    }
  }, [audioWireType]);





  // REMOVED: Duplicate onReady logic - consolidated below

  // REMOVED: Auto-capture logic - now triggered only by global Next button
  // The global Next button in CableBuilder will handle image generation
  
  // Keep the readiness check for the Next button
  useEffect(() => {
    // Check if all requirements are met for image capture
    const allConnectorsSelected = connectorPoints.every(point => connectors[point.id]?.id);
    const allLengthsSet = lengths.A && lengths.C && lengths.D && lengths.E && lengths.F;
    const allRequiredLengthsEdited = ['A', 'C', 'D', 'E', 'F'].every(id => editedFields.has(id));
    const noIECErrors = !iec12AWGError;
    
    const isReady = allConnectorsSelected && allLengthsSet && allRequiredLengthsEdited && noIECErrors;
    
    if (onReady) {
      onReady(isReady);
      
      // Track missing requirements for popup
      if (typeof onMissingRequirements === 'function') {
        const missingRequirements = [];
        
        // Check missing connectors
        connectorPoints.forEach(point => {
          if (!connectors[point.id]?.id) {
            missingRequirements.push(`Please select a connector for ${point.id}`);
          }
        });
        
        // Check missing lengths
        ['A', 'C', 'D', 'E', 'F'].forEach(id => {
          if (!lengths[id] || lengths[id] === undefined || lengths[id] === null || lengths[id] === '' || isNaN(lengths[id])) {
            missingRequirements.push(`Please set a length for ${id}`);
          } else if (!editedFields.has(id)) {
            missingRequirements.push(`Please interact with length field ${id} (click in the field)`);
          }
        });
        
        // Check IEC errors
        if (iec12AWGError) {
          missingRequirements.push('IEC connectors are not compatible with 12 AWG wire gauge. Please select 14 AWG or 16 AWG, or choose different power connectors.');
        }
        
        onMissingRequirements(missingRequirements);
      }
    }
  }, [connectors, lengths, editedFields, iec12AWGError, onReady, onMissingRequirements]);

  // Client-side image capture function (fallback)
  const captureClientSideImage = useCallback(async () => {
    if (!actualSvgRef?.current) {
      console.log('⚠️ [CLIENT-CAPTURE] No SVG ref available');
      return null;
    }

    try {
      console.log('🔄 [CLIENT-CAPTURE] Capturing client-side image...');
      
      const svgElement = actualSvgRef.current;
      
      // Get the SVG's explicit width and height attributes
      const svgWidth = svgElement.getAttribute('width') || '800';
      const svgHeight = svgElement.getAttribute('height') || '400';
      const viewBox = svgElement.getAttribute('viewBox');
      
      console.log('🔍 [CLIENT-CAPTURE] SVG element found:', {
        tagName: svgElement.tagName,
        id: svgElement.id,
        className: svgElement.className,
        width: svgWidth,
        height: svgHeight,
        viewBox: viewBox,
        offsetWidth: svgElement.offsetWidth,
        offsetHeight: svgElement.offsetHeight
      });

      // Try capturing the container first (more reliable)
      const container = svgElement.closest('.MuiBox-root') || svgElement.parentElement;
      
      if (container) {
        console.log('🔧 [CLIENT-CAPTURE] Capturing container instead of SVG directly');
        
        const canvas = await html2canvas(container, {
          backgroundColor: '#ffffff',
          scale: 2,
          useCORS: true,
          allowTaint: true,
          foreignObjectRendering: true,
          removeContainer: false,
          width: parseInt(svgWidth) || 800,
          height: parseInt(svgHeight) || 400
        });
        
        console.log('🔧 [CLIENT-CAPTURE] Container captured successfully');
        
        // Convert to blob
        const blob = await new Promise(resolve => {
          canvas.toBlob(resolve, 'image/png', 0.95);
        });

        console.log('✅ [CLIENT-CAPTURE] Client-side image captured successfully');
        return blob;
      } else {
        // Fallback to direct SVG capture
        console.log('🔧 [CLIENT-CAPTURE] No container found, falling back to direct SVG capture');
        
        const canvas = await html2canvas(svgElement, {
          width: parseInt(svgWidth) || 800,
          height: parseInt(svgHeight) || 400,
          backgroundColor: '#ffffff',
          scale: 2,
          useCORS: true,
          allowTaint: true,
          foreignObjectRendering: true,
          removeContainer: false,
          onclone: (clonedDoc) => {
            const clonedSvg = clonedDoc.querySelector('svg');
            if (clonedSvg) {
              // Set explicit dimensions for the cloned SVG
              clonedSvg.setAttribute('width', svgWidth || '800');
              clonedSvg.setAttribute('height', svgHeight || '400');
              
              // Preserve the original viewBox if it exists
              if (viewBox) {
                clonedSvg.setAttribute('viewBox', viewBox);
              } else {
                // Set a default viewBox based on width and height
                clonedSvg.setAttribute('viewBox', `0 0 ${svgWidth || 800} ${svgHeight || 400}`);
              }
              
              // Ensure xmlns is set
              clonedSvg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
              
              console.log('🔧 [CLIENT-CAPTURE] Cloned SVG prepared with:', {
                width: clonedSvg.getAttribute('width'),
                height: clonedSvg.getAttribute('height'),
                viewBox: clonedSvg.getAttribute('viewBox')
              });
              
              // Remove problematic attributes that cause html2canvas issues
              const mainGroup = clonedSvg.querySelector('g');
              if (mainGroup && mainGroup.getAttribute('transform')) {
                console.log('🔧 [CLIENT-CAPTURE] Removing transform from main group');
                mainGroup.removeAttribute('transform');
              }
              
              // Remove filter attributes
              const elementsWithFilter = clonedSvg.querySelectorAll('[filter]');
              elementsWithFilter.forEach(el => {
                console.log('🔧 [CLIENT-CAPTURE] Removing filter from:', el.tagName);
                el.removeAttribute('filter');
              });
              
              // Remove any interactive elements that might interfere with capture
              const interactiveElements = clonedSvg.querySelectorAll('[style*="cursor"], [onclick]');
              interactiveElements.forEach(el => {
                el.style.cursor = 'default';
                el.removeAttribute('onclick');
              });
            }
          }
        });

        console.log('🔧 [CLIENT-CAPTURE] Direct SVG capture completed');
        
        // Convert to blob
        const blob = await new Promise(resolve => {
          canvas.toBlob(resolve, 'image/png', 0.95);
        });

        console.log('✅ [CLIENT-CAPTURE] Client-side image captured successfully');
        return blob;
      }
    } catch (error) {
      console.error('❌ [CLIENT-CAPTURE] Error capturing client-side image:', error);
      return null;
    }
  }, [actualSvgRef]);

  // Register with the client image capture service
  useEffect(() => {
    const componentId = 'hybrid-connector-selection';
    
    console.log('🔧 [CLIENT-CAPTURE] Registering capture function for:', componentId);
    console.log('🔧 [CLIENT-CAPTURE] actualSvgRef status:', {
      hasRef: !!actualSvgRef,
      hasCurrent: !!actualSvgRef?.current,
      elementType: actualSvgRef?.current?.tagName,
      elementId: actualSvgRef?.current?.id
    });
    
    // Create a stable capture function that doesn't change on every render
    const stableCaptureFunction = async () => {
      console.log('🎯 [CLIENT-CAPTURE] Capture function called for:', componentId);
      
      if (!actualSvgRef?.current) {
        console.warn('❌ [CLIENT-CAPTURE] No SVG element found for capture');
        return null;
      }
      
      try {
        console.log('🔄 [CLIENT-CAPTURE] Starting client-side image capture...');
        
        // Add a small delay to ensure SVG is fully rendered
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Use the improved capture function
        return await captureClientSideImage();
        
      } catch (error) {
        console.error('❌ [CLIENT-CAPTURE] Error capturing client-side image:', error);
        return null;
      }
    };
    
    clientImageCaptureService.registerCaptureFunction(componentId, stableCaptureFunction);
    
    return () => {
      console.log('🗑️ [CLIENT-CAPTURE] Unregistering capture function for:', componentId);
      clientImageCaptureService.unregisterCaptureFunction(componentId);
    };
  }, [actualSvgRef]);

  // Expose capture function to parent component (legacy support)
  useEffect(() => {
    if (onImageCaptureReady) {
      onImageCaptureReady(captureClientSideImage);
    }
  }, [captureClientSideImage, onImageCaptureReady]);

  // Prepare SVG for Puppeteer capture using absolute URLs (same as working components)
  const prepareSvgForPuppeteer = useCallback(async (svgContent) => {
    try {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = svgContent;
      const svgElement = tempDiv.querySelector('svg');

      if (!svgElement) {
        console.warn('⚠️ [SVG-PREP] No SVG element found in content');
        return svgContent;
      }

      // Ensure the SVG has an ID for identification
      if (!svgElement.id) {
        svgElement.id = 'hybrid-connector-selection-svg';
      }

      const imageElements = svgElement.querySelectorAll('image');
      console.log(`🔄 [SVG-PREP] Found ${imageElements.length} image elements to prepare`);

      for (const img of imageElements) {
        const href = img.getAttribute('href');
        if (href) {
          console.log(`🔄 [SVG-PREP] Processing image: ${href}`);
          
          // Convert to absolute URL
          let absoluteUrl;
          if (href.startsWith('data:')) {
            // Keep data URLs as is
            absoluteUrl = href;
            console.log(`🔄 [SVG-PREP] Keeping data URL as is`);
          } else if (href.startsWith('http')) {
            // Already absolute URL
            absoluteUrl = href;
            console.log(`🔄 [SVG-PREP] Already absolute URL: ${absoluteUrl}`);
          } else {
            // Convert relative path to absolute URL
            const baseUrl = 'https://nlfx-cable.luknai.com';
            absoluteUrl = href.startsWith('/') 
              ? `${baseUrl}${href}`
              : `${baseUrl}/${href}`;
            console.log(`🔄 [SVG-PREP] Converted relative path to absolute URL: ${href} → ${absoluteUrl}`);
          }
          
          // Update the href attribute
          img.setAttribute('href', absoluteUrl);
        }
        
        // Remove shadow filter from image elements for Puppeteer capture
        if (img.getAttribute('filter')) {
          console.log(`🔄 [SVG-PREP] Removing filter from image element for Puppeteer capture`);
          img.removeAttribute('filter');
        }
      }
      
      console.log('✅ [SVG-PREP] SVG prepared for Puppeteer rendering with absolute URLs');
      return svgElement.outerHTML;
    } catch (error) {
      console.error('❌ [SVG-PREP] Error preparing SVG for Puppeteer:', error);
      return svgContent;
    }
  }, []);

  // Puppeteer-based image capture function (same as working components)
  const captureWithPuppeteer = useCallback(async (sku) => {
    console.log('🔄 [PUPPETEER-CAPTURE] Starting Puppeteer-based capture...');
    
    try {
      // Generate unique filename
      const timestamp = Date.now();
      const outputFileName = `${sku}_${timestamp}.png`;
      
      // Get the SVG content from the current DOM
      const svgElement = document.getElementById('hybrid-connector-selection-svg') || actualSvgRef?.current;
      if (!svgElement) {
        console.error('❌ [PUPPETEER-CAPTURE] SVG element not found in DOM');
        return null;
      }

      // Prepare SVG for Puppeteer capture by converting image paths to absolute URLs
      console.log('🔄 [PUPPETEER-CAPTURE] Preparing SVG for Puppeteer capture...');
      const svgWithAbsoluteUrls = await prepareSvgForPuppeteer(svgElement.outerHTML);
      
      console.log('📄 [PUPPETEER-CAPTURE] SVG content length:', svgWithAbsoluteUrls.length);
      
      // Call the server-side capture endpoint with SVG content
      console.log('📡 [PUPPETEER-CAPTURE] Sending request to:', 'https://nlfx-cable.luknai.com/api/capture-svg');
      console.log('📡 [PUPPETEER-CAPTURE] Request payload:', {
        svgContent: svgWithAbsoluteUrls.substring(0, 200) + '...', // Log first 200 chars for debugging
        outputFileName: outputFileName
      });
      
      const response = await fetch('https://nlfx-cable.luknai.com/api/capture-svg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          svgContent: svgWithAbsoluteUrls,
          outputFileName: outputFileName
        })
      });
      
      console.log('📡 [PUPPETEER-CAPTURE] Response status:', response.status);
      console.log('📡 [PUPPETEER-CAPTURE] Response headers:', Object.fromEntries(response.headers.entries()));
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('📡 [PUPPETEER-CAPTURE] Error response body:', errorText);
        throw new Error(`Server responded with ${response.status}: ${response.statusText} - ${errorText}`);
      }

      const result = await response.json();
      
      if (result.success) {
        console.log('✅ [PUPPETEER-CAPTURE] Puppeteer capture successful:', result.s3Url);
        return result.s3Url;
      } else {
        throw new Error(result.error || 'Unknown error from server');
      }
    } catch (error) {
      console.error('❌ [PUPPETEER-CAPTURE] Error during Puppeteer capture:', error);
      return null;
    }
  }, [actualSvgRef, prepareSvgForPuppeteer]);

  // REMOVED: Auto-capture logic - now triggered only by global Next button
  // The global Next button in CableBuilder will handle image generation

  // Initialize editedFields
  useEffect(() => {
    setEditedFields(new Set());
  }, []);

  // Calculate the longer of left and right branches for B calculation (like HybridPowerAudioAudio)
  const calculateLengthB = (lengths) => {
    // B = A - (longer of C/E + longer of D/F), with minimum of 6"
    const longerCE = Math.max(parseFloat(lengths['C'] || 0), parseFloat(lengths['E'] || 0));
    const longerDF = Math.max(parseFloat(lengths['D'] || 0), parseFloat(lengths['F'] || 0));
    const calculatedB = parseFloat(lengths['A'] || 0) - (longerCE + longerDF);
    return Math.max(6, calculatedB); // Minimum 6" for B
  };

  const calculateRequiredOverallLength = (lengths) => {
    // Calculate the minimum required overall length based on branch lengths + minimum B of 6"
    const longerCE = Math.max(parseFloat(lengths['C'] || 0), parseFloat(lengths['E'] || 0));
    const longerDF = Math.max(parseFloat(lengths['D'] || 0), parseFloat(lengths['F'] || 0));
    const minB = 6; // Minimum B length
    return longerCE + longerDF + minB;
  };

  // Calculate dynamic control points and endpoints for each branch first
  const cLen = PX_PER_INCH * (parseFloat(lengths['C']) || 0); // F branch (audio)
  const dLen = PX_PER_INCH * (parseFloat(lengths['D']) || 0); // G branch (audio)
  const eLen = PX_PER_INCH * (parseFloat(lengths['E']) || 0); // H branch (power)
  const fLen = PX_PER_INCH * (parseFloat(lengths['F']) || 0); // I branch (power)

  // Maximum visual length for SVG display (equivalent to ~50 inches visually)
  const MAX_VISUAL_LENGTH = 300;
  
  // Visual lengths - capped for reasonable SVG display
  const cVisual = Math.min(cLen, MAX_VISUAL_LENGTH);
  const dVisual = Math.min(dLen, MAX_VISUAL_LENGTH);
  const eVisual = Math.min(eLen, MAX_VISUAL_LENGTH);
  const fVisual = Math.min(fLen, MAX_VISUAL_LENGTH);

  // Define all SVG-related variables at the top
  const connW = 40;
  const connH = 40;

  // For very short lengths, make the branches visually even shorter
  const minBranch = 3; // visually minimum px for short lengths
  const cShort = Math.max(cVisual, minBranch);
  const dShort = Math.max(dVisual, minBranch);
  const eShort = Math.max(eVisual, minBranch);
  const fShort = Math.max(fVisual, minBranch);

  // Calculate endpoint positions using visual lengths
  const G_end = { x: 200 - cShort - 50, y: 120 };  // G uses C length
  const H_end = { x: 750 + dShort + 50, y: 120 };  // H uses D length
  const I_end = { x: 200 - eShort - 50, y: 380 };  // I uses E length
  const J_end = { x: 750 + fShort + 50, y: 380 };  // J uses F length

  // Calculate overall line positions
  const overallLineY = 60;

  // Calculate overall length - A is now the true overall
  const overallLen = parseFloat(lengths['A']) || 0;

  // Auto-scaling logic - simplified for better centering
  const pad = 30;
  const minX = Math.min(G_end.x, I_end.x);
  const maxX = Math.max(H_end.x, J_end.x);
  const contentWidth = maxX - minX;
  
  // Calculate actual content bounds including labels - ensure A label isn't cut off
  const minY = 60 - 30; // Account for Overall label above the line (overallLineY = 60)
  const maxY = Math.max(I_end.y, J_end.y) + 40 + 15; // Connector + label height
  const contentHeight = maxY - minY;
  
  // Calculate viewBox for proper centering with adequate padding for labels
  const viewBoxWidth = contentWidth + 2 * pad;
  const viewBoxHeight = contentHeight + 2 * pad; // More padding for labels
  const viewBoxX = minX - pad;
  const viewBoxY = minY - pad; // Full padding at top for A label
  
  // No transform needed - let SVG handle centering
  const groupStyle = {
    transition: 'all 0.3s ease',
  };

  // Dynamic label positions - positioned along each wire path using visual lengths
  const cLabel = { x: 200 - (cVisual * 0.6), y: 200 - (cVisual * 0.3) }; // Along C wire path (F) - following upward curve
  const dLabel = { x: 750 + (dVisual * 0.6), y: 200 - (dVisual * 0.3) }; // Along D wire path (G) - following upward curve
  const eLabel = { x: 200 - (eVisual * 0.6), y: 300 + (eVisual * 0.3) }; // Along E wire path (H) - following downward curve
  const fLabel = { x: 750 + (fVisual * 0.6), y: 300 + (fVisual * 0.3) }; // Along F wire path (I) - following downward curve

  // Calculate leftmost and rightmost points for overall length line
  const leftmostX = Math.min(G_end.x, I_end.x) - 20;
  const rightmostX = Math.max(H_end.x, J_end.x) + 20;

  // SVG interaction handlers with improved trackpad support
  const handleSvgWheel = useCallback((e) => {
    // Only handle wheel events if we're not over a form element
    const target = e.target;
    const isFormElement = target.closest('input, textarea, select, button, [role="button"]');
    
    if (isFormElement) {
      return; // Let the form element handle the event
    }
    
    // Check if this is a trackpad gesture (deltaY is small) vs mouse wheel (deltaY is large)
    const isTrackpadGesture = Math.abs(e.deltaY) < 50;
    
      if (isTrackpadGesture && e.ctrlKey) {
        // Ctrl + trackpad = zoom towards mouse pointer
        e.preventDefault();
        const delta = e.deltaY > 0 ? 0.9 : 1.1;
        // Reduce zoom sensitivity by 87.5% (multiply by 0.125)
        const sensitivityMultiplier = 0.125;
        const adjustedDelta = 1 + (delta - 1) * sensitivityMultiplier;
        const newScale = Math.max(0.5, Math.min(6, svgTransform.scale * adjustedDelta));
      
      // Get mouse position relative to SVG
      const svgRect = e.currentTarget.getBoundingClientRect();
      const mouseX = e.clientX - svgRect.left;
      const mouseY = e.clientY - svgRect.top;
      
      // Calculate zoom center (mouse position or SVG center if mouse is outside)
      const centerX = mouseX >= 0 && mouseX <= svgRect.width ? mouseX : svgRect.width / 2;
      const centerY = mouseY >= 0 && mouseY <= svgRect.height ? mouseY : svgRect.height / 2;
      
      // Calculate new transform to zoom towards the center point
      const scaleChange = newScale / svgTransform.scale;
      const newX = svgTransform.x - (centerX - svgTransform.x) * (scaleChange - 1);
      const newY = svgTransform.y - (centerY - svgTransform.y) * (scaleChange - 1);
      
      setSvgTransform({
        x: newX,
        y: newY,
        scale: newScale
      });
      } else if (!isTrackpadGesture) {
        // Mouse wheel = zoom towards mouse pointer
        e.preventDefault();
        const delta = e.deltaY > 0 ? 0.9 : 1.1;
        const newScale = Math.max(0.5, Math.min(6, svgTransform.scale * delta));
      
      // Get mouse position relative to SVG
      const svgRect = e.currentTarget.getBoundingClientRect();
      const mouseX = e.clientX - svgRect.left;
      const mouseY = e.clientY - svgRect.top;
      
      // Calculate zoom center (mouse position or SVG center if mouse is outside)
      const centerX = mouseX >= 0 && mouseX <= svgRect.width ? mouseX : svgRect.width / 2;
      const centerY = mouseY >= 0 && mouseY <= svgRect.height ? mouseY : svgRect.height / 2;
      
      // Calculate new transform to zoom towards the center point
      const scaleChange = newScale / svgTransform.scale;
      const newX = svgTransform.x - (centerX - svgTransform.x) * (scaleChange - 1);
      const newY = svgTransform.y - (centerY - svgTransform.y) * (scaleChange - 1);
      
      setSvgTransform({
        x: newX,
        y: newY,
        scale: newScale
      });
    }
    // For trackpad without Ctrl, let the browser handle scrolling
  }, [svgTransform]);

  // Handle wheel events with proper passive listener
  useEffect(() => {
    if (!actualSvgRef) return;
    const svgElement = actualSvgRef.current;
    if (!svgElement) return;

    // Add event listener with passive: false to allow preventDefault
    svgElement.addEventListener('wheel', handleSvgWheel, { passive: false });

    return () => {
      svgElement.removeEventListener('wheel', handleSvgWheel);
    };
  }, [handleSvgWheel, actualSvgRef]);

  const handleSvgMouseDown = (e) => {
    // Only start panning if we're not over a form element
    const target = e.target;
    const isFormElement = target.closest('input, textarea, select, button, [role="button"]');
    
    if (isFormElement) {
      return;
    }
    
    // Only start panning on left mouse button
    if (e.button === 0) {
      setIsPanning(true);
      setLastPanPoint({ x: e.clientX, y: e.clientY });
    }
  };

  const handleSvgMouseMove = useCallback((e) => {
    if (!isPanning) return;
    
    // Prevent default browser behavior to ensure smooth panning
    e.preventDefault();
    
    // Only pan if we're not over a form element
    const target = e.target;
    const isFormElement = target.closest('input, textarea, select, button, [role="button"]');
    
    if (isFormElement) {
      setIsPanning(false);
      return;
    }
    
    const deltaX = e.clientX - lastPanPoint.x;
    const deltaY = e.clientY - lastPanPoint.y;
    
    // Enhanced panning for trackpad gestures
    // Trackpad events often have different movement characteristics
    const isTrackpadGesture = e.movementX !== undefined && e.movementY !== undefined;
    
    let adjustedDeltaX = deltaX;
    let adjustedDeltaY = deltaY;
    
    // For trackpad gestures, use movementX/Y if available for more responsive panning
    if (isTrackpadGesture && (e.movementX !== 0 || e.movementY !== 0)) {
      adjustedDeltaX = e.movementX;
      adjustedDeltaY = e.movementY;
    }
    
    // Use functional update to avoid dependency on current state
    setSvgTransform(prev => ({
      ...prev,
      x: prev.x + adjustedDeltaX,
      y: prev.y + adjustedDeltaY
    }));
    setLastPanPoint({ x: e.clientX, y: e.clientY });
  }, [isPanning, lastPanPoint]);

  const handleSvgMouseUp = () => {
    setIsPanning(false);
  };

  // Handle touch events for mobile
  const handleSvgTouchStart = (e) => {
    if (e.touches.length === 1) {
      // Single touch - start panning
      const touch = e.touches[0];
      setIsPanning(true);
      setLastPanPoint({ x: touch.clientX, y: touch.clientY });
    } else if (e.touches.length === 2) {
      // Two finger touch - prevent default to avoid browser zoom
      e.preventDefault();
    }
  };

  const handleSvgTouchMove = useCallback((e) => {
    if (e.touches.length === 1 && isPanning) {
      // Single touch panning - prevent default to ensure smooth panning
      e.preventDefault();
      const touch = e.touches[0];
      const deltaX = touch.clientX - lastPanPoint.x;
      const deltaY = touch.clientY - lastPanPoint.y;
      
      // Enhanced sensitivity for touch panning
      // Touch events may need different sensitivity than mouse events
      const sensitivity = 1.0; // Can be adjusted if needed
      
      setSvgTransform(prev => ({
        ...prev,
        x: prev.x + (deltaX * sensitivity),
        y: prev.y + (deltaY * sensitivity)
      }));
      setLastPanPoint({ x: touch.clientX, y: touch.clientY });
    } else if (e.touches.length === 2) {
      // Two finger pinch zoom towards center of pinch
      e.preventDefault();
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      
      // Calculate center of pinch gesture
      const pinchCenterX = (touch1.clientX + touch2.clientX) / 2;
      const pinchCenterY = (touch1.clientY + touch2.clientY) / 2;
      
      // Get SVG bounds
      const svgRect = e.currentTarget.getBoundingClientRect();
      const centerX = pinchCenterX - svgRect.left;
      const centerY = pinchCenterY - svgRect.top;
      
      if (e.scale) {
        // Reduce zoom sensitivity by 87.5% (multiply by 0.125)
        const sensitivityMultiplier = 0.125;
        const adjustedScale = 1 + (e.scale - 1) * sensitivityMultiplier;
        const newScale = Math.max(0.5, Math.min(6, svgTransform.scale * adjustedScale));
        
        // Calculate new transform to zoom towards the pinch center
        const scaleChange = newScale / svgTransform.scale;
        const newX = svgTransform.x - (centerX - svgTransform.x) * (scaleChange - 1);
        const newY = svgTransform.y - (centerY - svgTransform.y) * (scaleChange - 1);
        
        setSvgTransform({
          x: newX,
          y: newY,
          scale: newScale
        });
      }
    }
  }, [isPanning, lastPanPoint, svgTransform]);

  const handleSvgTouchEnd = () => {
    setIsPanning(false);
  };

  // Helper function to generate straight wire path
  const generateStraightWire = (x1, x2, y, length) => {
    return `M ${x1} ${y} L ${x2} ${y}`;
  };

  // Update handleLengthChange for Overall to only update on blur or Enter
  const handleOverallInputChange = (val) => {
    // Allow free typing without validation - only store the input value
    setOverallInput(val);
    setLastManualEdit('A'); // Track this as a manual edit
  };

  const commitOverallInput = () => {
    let v = parseFloat(overallInput);
    if (isNaN(v) || v < 0) {
      // If invalid input, reset to current value
      const displayValue = unit === 'ft' ? parseFloat(overallLen).toFixed(2).replace(/\.00$/, '') : overallLen.toString();
      setOverallInput(displayValue);
      return;
    }
    
    if (unit === 'ft') {
      // Input is in feet, convert to inches for internal storage
      // Cap at 500 feet before conversion
      if (v > 500) v = 500;
      v = v * 12; // Convert feet to inches
      // Round to nearest whole inch
      v = Math.round(v);
    } else {
      // Input is in inches, cap at 6000 inches (500 feet)
      if (v > 6000) v = 6000;
      // Round to nearest whole inch
      v = Math.round(v);
    }
    
    // Calculate the minimum required overall length based on branch lengths
    const longerCE = Math.max(parseFloat(lengths['C'] || 0), parseFloat(lengths['E'] || 0));
    const longerDF = Math.max(parseFloat(lengths['D'] || 0), parseFloat(lengths['F'] || 0));
    const minB = 6; // Minimum B length
    const minRequiredOverall = longerCE + longerDF + minB;
    
    // Ensure overall length is never less than minimum required
    if (v < minRequiredOverall) {
      v = minRequiredOverall;
      // Update the input field to show the corrected value
      const displayValue = unit === 'ft' ? (parseFloat(v) / 12).toFixed(2).replace(/\.00$/, '') : v.toString();
      setOverallInput(displayValue);
    }
    
    // Use the same logic as handleLengthChange for consistency
    const newLengths = { ...lengths };
    newLengths['A'] = Math.min(v, 6000);
    newLengths['Overall'] = newLengths['A']; // Also set Overall for review page
    newLengths['B'] = calculateLengthB({ ...newLengths, A: newLengths['A'] });
    
    setLengths(newLengths);
            setEditedFields(prev => new Set([...prev, 'A', 'B'])); // Mark A and B as edited
    setLastManualEdit('A'); // Track this as a manual edit of A
    
    // Update the overallInput display value to reflect the rounded value
    const displayValue = unit === 'ft' ? (parseFloat(v) / 12).toFixed(2).replace(/\.00$/, '') : v.toString();
    setOverallInput(displayValue);
    
    // Create the hybrid connectors structure expected by pricing service
    const hybridConnectors = {
      connectors: connectors,
      lengths: newLengths,
      cableColor: cableColor
    };
    
    stableOnChange({ 
      connectors, 
      lengths: newLengths, 
      specialInstructions,
      powerWireGauge,
      cableColor,
      hybridConnectors // Add this for pricing service compatibility
    });
  };

  // Sync overallInput with overallLen and unit when they change
  useEffect(() => {
    // Don't auto-update if the user has manually edited A
    if (lastManualEdit === 'A') {
      return;
    }
    
    // Only update overallInput if it's not currently being edited
    const currentInputValue = parseFloat(overallInput);
    const calculatedValue = overallLen;
    const displayValue = unit === 'ft' ? (parseFloat(calculatedValue) / 12).toFixed(2).replace(/\.00$/, '') : calculatedValue;
    
    // Convert currentInputValue to the same unit as calculatedValue for comparison
    const currentInputInSameUnit = unit === 'ft' ? currentInputValue * 12 : currentInputValue;
    
    // Only update if the difference is significant (more than 1 unit) to avoid interfering with typing
    if (isNaN(currentInputValue) || Math.abs(currentInputInSameUnit - calculatedValue) > 1) {
      setOverallInput(displayValue);
    }
  }, [overallLen, unit, lastManualEdit, overallInput]);

  // Sync overallInput when overallLen changes from value prop
  useEffect(() => {
    if (value.lengths?.A && lastManualEdit !== 'A') {
      const displayValue = unit === 'ft' ? (value.lengths.A / 12).toFixed(2).replace(/\.00$/, '') : value.lengths.A.toString();
      setOverallInput(displayValue);
    }
  }, [value.lengths?.A, unit, lastManualEdit]);







  // Update isEdited logic for green checks
  const isEdited = (id) => {
    return editedFields.has(id);
  };

  const handleLengthFocus = (id) => {
    setEditedFields(prev => new Set([...prev, id]));
  };

  const handlePointClick = (id) => {
    setSelectedPoint(id);
    setModalOpen(true);
  };
  
  // Clear loaded connectors ref when modal closes
  const handleModalClose = useCallback(() => {
    setModalOpen(false);
    loadedConnectorsRef.current = {}; // Clear the ref when modal closes
  }, []);

  // Filter connectors based on compatibility with connectors of the same type only
  const getFilteredConnectors = useCallback(async (forPoint) => {
    const point = connectorPoints.find(p => p.id === forPoint);
    if (!point) {
      return [];
    }
    
    // Get all connectors from compatibility matrix for this cable type
    // For hybrid builds, hybrid points should show 'audio' connectors, power points show 'power' connectors
    const cableType = point.type === 'hybrid' ? 'audio' : 'power';
    
    // Use buildConnectorFamilies to get connectors with both specific purpose AND 'hybrid'
    const families = buildConnectorFamilies(cableType, { requireHybrid: true });
    
    // Flatten all variants from all families into a single array
    const baseConnectors = families.flatMap(family => family.variants);
    
    const pairedPoint = pairs[forPoint];
    const pairedConnector = connectors[pairedPoint];
    
    if (!pairedConnector?.id) {
      return baseConnectors;
    }
    
    const filteredConnectors = [];
    for (const connector of baseConnectors) {
      // Prevent same connector on both ends for power
      if (point.type === 'power' && connector.id === pairedConnector.id) {
        console.log('[DEBUG] Skipping same connector for power:', connector.id);
        continue;
      }
      
      try {
        // For the paired connector, we need to consider the configuration variant
        let pairedId = pairedConnector.id;
        
        // If the paired connector has a configuration that's different from 'standard',
        // we need to find the variant connector ID
        if (pairedConnector.configuration && pairedConnector.configuration !== 'standard') {
          const variantConnector = findConnectorVariant(pairedConnector, pairedConnector.configuration);
          if (variantConnector && variantConnector.id) {
            pairedId = variantConnector.id;
          }
        }
        
        const compatibilityResult = areConnectorsCompatible(pairedId, connector.id);
        
        // Check wire gauge compatibility if both connectors are power connectors
        if (compatibilityResult && compatibilityResult.compatible && connector.purpose === 'power' && pairedConnector.purpose === 'power') {
          try {
            getConnectorCompatibilityWarningSync(connector.id, pairedId);
            // If there's a wire gauge warning, still consider it compatible but with warning
            // Don't filter out connectors with wire gauge warnings
          } catch (wireGaugeError) {
            console.warn('Error checking wire gauge compatibility:', wireGaugeError);
          }
        }
        
        if (compatibilityResult && compatibilityResult.compatible) {
          filteredConnectors.push(connector);
        }
      } catch (error) {
        console.warn('[ERROR] Error checking compatibility for', connector.id, ':', error);
      }
    }
    
    return filteredConnectors;
  }, [connectors.G?.id, connectors.H?.id, connectors.I?.id, connectors.J?.id]);

  const handleConnectorSelect = useCallback((connector) => {
    // Map mountType from compatibility matrix to configuration value
    let autoConfiguration = 'standard';
    if (connector.mountType === 'rightangle' || connector.mountType === 'right-angle') {
      autoConfiguration = 'right-angle';
    } else if (connector.mountType === 'panel') {
      autoConfiguration = 'panel-mount';
    } else if (connector.mountType === 'cable') {
      autoConfiguration = 'standard';
    }
    
    // Convert to legacy format that the parent expects
    const legacyConnector = {
      id: connector.id,
      label: connector.description || connector.Label || connector.label || connector.id,
      gender: connector.gender,
      image: connector.image,
      family: connector.family,
      type: connector.purpose?.split(',')[0] || connector.type || 'audio', // Use first purpose
      brand: 'Professional',
      configuration: autoConfiguration, // Use mapped configuration instead of always 'standard'
      skuCode: connector.skuCode,
      // Add all the fields that the parent expects
      description: connector.description || connector.Label || connector.label || connector.id,
      mountType: connector.mountType || 'cable',
      poles: connector.poles || 2,
      // Ensure all required fields are present
      Label: connector.description || connector.Label || connector.label || connector.id
    };
    
    const newConnectors = {
      ...connectors,
      [selectedPoint]: legacyConnector
    };
    
    setConnectors(newConnectors);
    handleModalClose();
    
    // Create the hybrid connectors structure expected by pricing service
    const hybridConnectors = {
      connectors: newConnectors,
      lengths: lengths,
      cableColor: cableColor
    };
    
    stableOnChange({ 
      connectors: newConnectors, 
      lengths, 
      specialInstructions,
      powerWireGauge,
      audioWireType,
      cableColor,
      hybridConnectors // Add this for pricing service compatibility
    });
  }, [connectors, selectedPoint, lengths, specialInstructions, powerWireGauge, audioWireType, stableOnChange, handleModalClose]);

  // Add missing functions that are referenced but not defined
  const handleInstructionsChange = (e) => {
    setSpecialInstructions(e.target.value);
    // Create the hybrid connectors structure expected by pricing service
    const hybridConnectors = {
      connectors: connectors,
      lengths: lengths
    };
    
    stableOnChange({ 
      connectors, 
      lengths, 
      specialInstructions: e.target.value,
      powerWireGauge,
      audioWireType,
      cableColor,
      hybridConnectors // Add this for pricing service compatibility
    });
  };

  const handlePowerWireGaugeChange = (e) => {
    // Power wire gauge is fixed at 14AWG for hybrid cables - this function should not be called
    console.warn('Power wire gauge should not be changeable for hybrid cables');
    // Ignore the change and keep it at 14AWG
    stableOnChange({ connectors, lengths, specialInstructions, powerWireGauge: '14AWG', audioWireType, cableColor });
  };

  const handleAudioWireTypeChange = (e) => {
    // Audio wire type is fixed at PSH for hybrid cables - this function should not be called
    console.warn('Audio wire type should not be changeable for hybrid cables');
    // Ignore the change and keep it at PSH
    stableOnChange({ connectors, lengths, specialInstructions, powerWireGauge, audioWireType: 'PSH', cableColor });
  };

  const handleCableColorChange = (event, newColor) => {
    if (newColor === null) return; // Prevent deselection
    
    setCableColor(newColor);
    
    // Create the hybrid connectors structure expected by pricing service
    const hybridConnectors = {
      connectors: connectors,
      lengths: lengths,
      cableColor: newColor
    };
    
    stableOnChange({ 
      connectors, 
      lengths, 
      specialInstructions,
      powerWireGauge,
      audioWireType,
      cableColor: newColor,
      hybridConnectors // Add this for pricing service compatibility
    });
  };

  const handleLengthChange = (id, val) => {
    let v = parseFloat(val);
    if (isNaN(v) || v < 0) v = 0;
    if (unit === 'ft') {
      // Cap at 500 feet
      if (v > 500) v = 500;
      v = v * 12; // Convert feet to inches
      // Round to nearest whole inch
      v = Math.round(v);
    } else {
      // Cap at 6000 inches (500 feet)
      if (v > 6000) v = 6000;
      // Round to nearest whole inch
      v = Math.round(v);
    }
    setEditedFields(prev => new Set([...prev, id]));
    
    const newLengths = { ...lengths };
    let updateA = false;
    
    if (id === 'A') {
      // When overall length changes, cap at 6000 inches (500 feet)
      newLengths['A'] = Math.min(v, 6000);
      newLengths['Overall'] = newLengths['A']; // Also set Overall for review page
      newLengths['B'] = calculateLengthB({ ...newLengths, A: newLengths['A'] });
    } else if (['C', 'D', 'E', 'F'].includes(id)) {
      // When any branch length changes, update it (with 500 foot cap)
      newLengths[id] = Math.min(v, 6000);
      
      // Calculate the required overall length based on the new lengths
      const requiredOverall = calculateRequiredOverallLength(newLengths);
      
      // If the required overall length is greater than current A, update A (with 500 foot cap)
      if (requiredOverall > (newLengths['A'] || 0)) {
        newLengths['A'] = Math.min(requiredOverall, 6000);
        newLengths['Overall'] = newLengths['A']; // Also set Overall for review page
        updateA = true;
      }
      
      // Recalculate B based on the new lengths (and potentially updated A)
      newLengths['B'] = calculateLengthB(newLengths);
      
      // Mark as edited to show green checkmark immediately
      setEditedFields(prev => new Set([...prev, id]));
      if (updateA) {
        setEditedFields(prev => new Set([...prev, 'A']));
        // Update the overallInput field to reflect the new A value
        const newAValue = newLengths['A'];
        const displayValue = unit === 'ft' ? (parseFloat(newAValue) / 12).toFixed(2).replace(/\.00$/, '') : newAValue.toString();
        setOverallInput(displayValue);
      }
    } else if (id === 'B') {
      // B is read-only, don't allow direct changes
      return;
    }
    
    setLengths(newLengths);
    
    // Create the hybrid connectors structure expected by pricing service
    const hybridConnectors = {
      connectors: connectors,
      lengths: newLengths,
      cableColor: cableColor
    };
    
    stableOnChange({ 
      connectors, 
      lengths: newLengths, 
      specialInstructions,
      powerWireGauge,
      audioWireType,
      cableColor,
      hybridConnectors // Add this for pricing service compatibility
    });
  };

  const handleConfigurationChange = (pointId, configuration, event) => {
    const currentConnector = connectors[pointId];
    if (!currentConnector) return;
    
    // Find the correct connector variant for this configuration
    const newConnector = findConnectorVariant(currentConnector, configuration);
    
    // Check compatibility with the paired connector before making the change
    const pairedPointId = pairs[pointId];
    const pairedConnector = connectors[pairedPointId];
    
    if (pairedConnector?.id) {
      // For the paired connector, we need to consider the configuration variant
      let pairedId = pairedConnector.id;
      
      // If the paired connector has a configuration that's different from 'standard',
      // we need to find the variant connector ID
      if (pairedConnector.configuration && pairedConnector.configuration !== 'standard') {
        const variantConnector = findConnectorVariant(pairedConnector, pairedConnector.configuration);
        if (variantConnector && variantConnector.id) {
          pairedId = variantConnector.id;
        }
      }
      
      const compatibilityResult = areConnectorsCompatible(pairedId, newConnector.id);
      
      // Check wire gauge compatibility if both connectors are power connectors
      if (compatibilityResult && compatibilityResult.compatible && newConnector.purpose === 'power' && pairedConnector.purpose === 'power') {
        try {
          const wireGaugeWarning = getConnectorCompatibilityWarningSync(newConnector.id, pairedId);
          if (wireGaugeWarning) {
            console.warn('Wire gauge compatibility warning:', wireGaugeWarning.message);
            // Don't prevent the change, just log the warning
          }
        } catch (wireGaugeError) {
          console.warn('Error checking wire gauge compatibility:', wireGaugeError);
        }
      }
      
      if (!compatibilityResult) {
        // If areConnectorsCompatible returns null, it means no compatibility rule exists = incompatible
        const message = 'Not compatible with paired connector';
        setTooltipMessage(`Configuration change prevented: ${message}`);
        setTooltipAnchor(event?.currentTarget);
        setTooltipOpen(true);
        
        // Auto-hide tooltip after 3 seconds
        setTimeout(() => {
          setTooltipOpen(false);
        }, 3000);
        
        return; // Don't update the state
      }
      
      if (compatibilityResult && !compatibilityResult.compatible) {
        const message = compatibilityResult.reason || compatibilityResult.message || 'Incompatible with paired connector';
        setTooltipMessage(`Configuration change prevented: ${message}`);
        setTooltipAnchor(event?.currentTarget);
        setTooltipOpen(true);
        
        // Auto-hide tooltip after 3 seconds
        setTimeout(() => {
          setTooltipOpen(false);
        }, 3000);
        
        return; // Don't update the state
      }
    }
    
    const newConnectors = {
      ...connectors,
      [pointId]: newConnector
    };
    
    setConnectors(newConnectors);
    
    // Create the hybrid connectors structure expected by pricing service
    const hybridConnectors = {
      connectors: newConnectors,
      lengths: lengths,
      cableColor: cableColor
    };
    
    stableOnChange({ 
      connectors: newConnectors, 
      lengths, 
      specialInstructions,
      powerWireGauge,
      audioWireType,
      cableColor,
      hybridConnectors // Add this for pricing service compatibility
    });
  };

  const handleUnitChange = (event, newUnit) => {
    if (newUnit) {
      setUnit(newUnit);
      // Update the overallInput display value based on the new unit
      const currentLengthA = lengths['A'] || 0;
      if (newUnit === 'ft') {
        setOverallInput((currentLengthA / 12).toFixed(2).replace(/\.00$/, ''));
      } else {
        setOverallInput(String(currentLengthA));
      }
    }
  };

  // Helper function to check if a configuration variant exists for a connector
  // Now imported from utilityService.js - uses compatibility matrix as single source of truth
  // const getAvailableConfigurations = (connector) => { ... };

  // Go back functions
  // const handleBackToFamilies = () => {
  //   setSelectedPoint(null);
  // };

  // Add compatibility state at the top level for modal and summary
  const [compatibilityResults, setCompatibilityResults] = useState({});



  // Helper function to get available configurations for a specific connector
  const getConfigurationsForConnector = useCallback((connector) => {
    if (!connector?.id) return { standard: true, 'right-angle': true, 'panel-mount': true };
    
    // Get the current connector's data from the compatibility matrix
    const currentConnectorData = compatibilityMatrix[connector.id];
    if (!currentConnectorData) return { standard: true, 'right-angle': true, 'panel-mount': true };
    
    const { family, type, purpose, gender, poles } = currentConnectorData;
    
    // Check which configurations exist for this connector family/type/purpose/gender/poles
    const configurations = {
      standard: false,
      'right-angle': false,
      'panel-mount': false
    };
    
    // Search through the compatibility matrix for variants
    for (const [, connectorData] of Object.entries(compatibilityMatrix)) {
      // Handle multi-purpose parsing for compatibility
      const connectorPurposes = connectorData.purpose ? 
        connectorData.purpose.split(',').map(p => p.trim().toLowerCase()) : 
        [];
      const targetPurposes = purpose ? 
        purpose.split(',').map(p => p.trim().toLowerCase()) : 
        [];
      
      // For hybrid builds, we need to ensure the variant has 'hybrid' in its purpose
      const hasHybridPurpose = connectorPurposes.includes('hybrid');
      
      if (connectorData.family === family && 
          connectorData.type === type &&
          connectorData.gender === gender &&
          connectorData.poles === poles &&
          connectorPurposes.some(p => targetPurposes.includes(p)) &&
          hasHybridPurpose) { // Ensure variant has 'hybrid' purpose for hybrid builds
        
        // Check which mount type this variant has
        if (connectorData.mountType === 'cable') {
          configurations.standard = true;
        } else if (connectorData.mountType === 'rightangle' || connectorData.mountType === 'right-angle') {
          configurations['right-angle'] = true;
        } else if (connectorData.mountType === 'panel') {
          configurations['panel-mount'] = true;
        }
      }
    }
    
    return configurations;
  }, []);

  // Helper function to check if a connector is an IEC connector
function isIECConnector(connector) {
  if (!connector?.id) return false;
  return connector.id.toLowerCase().includes('iec') || 
         connector.family?.toLowerCase().includes('iec') ||
         connector.type?.toLowerCase().includes('iec');
}

  // Helper function to check if a configuration would be incompatible with the paired connector
  const checkConfigurationCompatibility = useCallback((pointId, configuration) => {
    const currentConnector = connectors[pointId];
    if (!currentConnector?.id) return null;
    
    // First check if this configuration actually exists for this connector
    const currentConnectorData = compatibilityMatrix[currentConnector.id];
    if (!currentConnectorData) return null;
    
    const { family, type, purpose, poles } = currentConnectorData;
    const targetMountType = mapConfigurationToMountType(configuration);
    
    // Check if a variant with this configuration actually exists
    let variantExists = false;
    for (const [, connectorData] of Object.entries(compatibilityMatrix)) {
      // Handle multi-purpose parsing for compatibility
      const connectorPurposes = connectorData.purpose ? 
        connectorData.purpose.split(',').map(p => p.trim().toLowerCase()) : 
        [];
      const targetPurposes = purpose ? 
        purpose.split(',').map(p => p.trim().toLowerCase()) : 
        [];
      
      // For hybrid builds, we need to ensure the variant has 'hybrid' in its purpose
      // Check if any of the connector's purposes match any of the target purposes
      const purposeMatches = connectorPurposes.some(connectorPurpose => 
        targetPurposes.includes(connectorPurpose)
      );
      
      // For hybrid builds, also check that the variant has 'hybrid' in its purpose
      const hasHybridPurpose = connectorPurposes.includes('hybrid');
      
      if (connectorData.family === family && 
          connectorData.type === type &&
          connectorData.poles === poles &&
          purposeMatches &&
          hasHybridPurpose && // Ensure variant has 'hybrid' purpose for hybrid builds
          (connectorData.mountType === targetMountType || 
           (targetMountType === 'rightangle' && connectorData.mountType === 'right-angle'))) {
        variantExists = true;
        break;
      }
    }
    
    // If the configuration doesn't exist, return null (will be disabled, not red)
    if (!variantExists) return null;
    
    // Find the connector variant for this configuration
    const newConnector = findConnectorVariant(currentConnector, configuration);
    if (!newConnector) return null;
    
    // Check compatibility with the paired connector
    const pairedPointId = pairs[pointId];
    const pairedConnector = connectors[pairedPointId];
    if (!pairedConnector?.id) return null;
    
    // For the paired connector, we need to consider the configuration variant
    let pairedId = pairedConnector.id;
    
    // If the paired connector has a configuration that's different from 'standard',
    // we need to find the variant connector ID
    if (pairedConnector.configuration && pairedConnector.configuration !== 'standard') {
      const variantConnector = findConnectorVariant(pairedConnector, pairedConnector.configuration);
      if (variantConnector && variantConnector.id) {
        pairedId = variantConnector.id;
      }
    }
    
    const compatResult = areConnectorsCompatible(pairedId, newConnector.id);
    
    if (!compatResult) {
      // If areConnectorsCompatible returns null, it means no compatibility rule exists = incompatible
      return {
        incompatible: true,
        reason: 'Not compatible with paired connector'
      };
    }
    
    if (compatResult && !compatResult.compatible) {
      return {
        incompatible: true,
        reason: compatResult.reason || compatResult.message || 'Incompatible with paired connector'
      };
    }
    
    return { incompatible: false };
  }, [connectors.G?.id, connectors.G?.configuration, connectors.H?.id, connectors.H?.configuration, connectors.I?.id, connectors.I?.configuration, connectors.J?.id, connectors.J?.configuration]);

  // Update compatibility state when connectors or modal selection changes
  useEffect(() => {
    // For each connector point, update compatibility with its pair
    const pairs = { 'G': 'H', 'H': 'G', 'I': 'J', 'J': 'I' };
    const results = {};
    for (const pointId of Object.keys(connectors)) {
      const pairedId = pairs[pointId];
      const connector = connectors[pointId];
      const paired = connectors[pairedId];
      
      if (connector?.id && paired?.id) {
        const result = areConnectorsCompatible(paired.id, connector.id);
        
        // Check wire gauge compatibility if both connectors are power connectors
        if (result && result.compatible && connector.purpose === 'power' && paired.purpose === 'power') {
          try {
            const wireGaugeWarning = getConnectorCompatibilityWarningSync(connector.id, paired.id);
            if (wireGaugeWarning) {
              // Add wire gauge warning to the result
              result.wireGaugeWarning = wireGaugeWarning;
            }
          } catch (wireGaugeError) {
            console.warn('Error checking wire gauge compatibility:', wireGaugeError);
          }
        }
        
        results[connector.id] = result;
        setCompatibilityResults(prev => ({ ...prev, [connector.id]: result }));
      }
    }
  }, [connectors.G?.id, connectors.H?.id, connectors.I?.id, connectors.J?.id, modalOpen, selectedPoint]);

  // Helper: clear connector for a specific point
  const handleClearConnector = useCallback((pointId) => {
    const updatedConnectors = { ...connectors };
    delete updatedConnectors[pointId];
    setConnectors(updatedConnectors);
    
    // Create the hybrid connectors structure expected by pricing service
    const hybridConnectors = {
      connectors: updatedConnectors,
      lengths: lengths,
      cableColor: cableColor
    };
    
    stableOnChange({ 
      connectors: updatedConnectors, 
      lengths, 
      specialInstructions,
      powerWireGauge,
      cableColor,
      hybridConnectors
    });
  }, [connectors, lengths, specialInstructions, powerWireGauge, stableOnChange]);

  // Load filtered connectors when modal opens
  useEffect(() => {
    if (modalOpen && selectedPoint) {
      // Create a key for this specific point and connector state
      const currentConnectorIds = [connectors.G?.id, connectors.H?.id, connectors.I?.id, connectors.J?.id].filter(Boolean).sort().join(',');
      const loadKey = `${selectedPoint}-${currentConnectorIds}`;
      
      // Check if we've already loaded connectors for this exact state
      if (loadedConnectorsRef.current[loadKey]) {
        return;
      }
      
      const loadFilteredConnectors = async () => {
        const connectors = await getFilteredConnectors(selectedPoint);
        
        // Mark this state as loaded
        loadedConnectorsRef.current[loadKey] = true;
        
        // Only update if the connectors are actually different
        setFilteredConnectors(prevConnectors => {
          // Check if the connectors are the same
          if (prevConnectors.length === connectors.length) {
            const prevIds = prevConnectors.map(c => c.id).sort();
            const newIds = connectors.map(c => c.id).sort();
            if (JSON.stringify(prevIds) === JSON.stringify(newIds)) {
              return prevConnectors; // Return the same reference
            }
          }
          return connectors;
        });
      };
      loadFilteredConnectors();
    }
  }, [modalOpen, selectedPoint, getFilteredConnectors]);



  return (
    <Box sx={{ mt: 3, position: 'relative' }}>
      {/* Reset button */}
      <Button
        variant="outlined"
        color="secondary"
        size="small"
        sx={{ position: 'absolute', top: 0, right: 0, zIndex: 2 }}
        onClick={() => {
          setConnectors({});
          setLengths({ ...defaultLengths });
          setSpecialInstructions('');
          setEditedFields(new Set()); // Reset all edited states
          setLastManualEdit(null); // Reset manual edit tracking
          setPowerWireGauge('14AWG');
          setOverallInput(unit === 'ft'
            ? (defaultLengths.A / 12).toFixed(2).replace(/\.00$/, '')
            : String(defaultLengths.A)
          );
          // Create the hybrid connectors structure expected by pricing service
          const hybridConnectors = {
            connectors: {},
            lengths: { ...defaultLengths },
            cableColor: 'black'
          };
          
          stableOnChange({ 
            connectors: {}, 
            lengths: { ...defaultLengths }, 
            specialInstructions: '',
            powerWireGauge: '14AWG', // Always 14AWG for hybrid cables
            audioWireType: 'PSH', // Always PSH for hybrid cables
            cableColor: 'black', // Always black for hybrid cables
            hybridConnectors // Add this for pricing service compatibility
          });
        }}
      >
        Reset
      </Button>
      
      <Typography variant="h5" gutterBottom>
        Configure Hybrid Cable Connectors
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Configure your hybrid cable connectors. Points G and H are for audio connectors, I and J are for power connectors.
      </Typography>

      {/* Note: Price display is handled by the global RealTimePriceEstimator component */}

      {/* IEC + 12 AWG Compatibility Error Alert */}
      {iec12AWGError && (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }}
          icon={<WarningIcon />}
        >
          <Typography variant="body2" fontWeight="bold">
            Incompatible Configuration Detected
          </Typography>
          <Typography variant="body2">
            IEC connectors are not compatible with 12 AWG wire gauge. Please select 14 AWG or 16 AWG, or choose different power connectors.
          </Typography>
        </Alert>
      )}

      {/* Unit toggle selector - Power Wire Gauge (14AWG) and Audio Wire Type are set automatically for hybrid cables */}
      <Box sx={{ mb: 2, display: 'flex', gap: 3, alignItems: 'center' }}>
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Length Units:
          </Typography>
          <ToggleButtonGroup
            value={unit}
            exclusive
            onChange={handleUnitChange}
            aria-label="unit toggle"
            size="small"
          >
            <ToggleButton value="in" aria-label="inches">Inches</ToggleButton>
            <ToggleButton value="ft" aria-label="feet">Feet</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        
        {/* Cable Color Selector - Only show if color choice is offered */}
        {offerColorChoice && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Cable Color:
            </Typography>
            <ToggleButtonGroup
              value={cableColor}
              exclusive
              onChange={handleCableColorChange}
              aria-label="cable color toggle"
              size="small"
            >
              <ToggleButton value="black" aria-label="black">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 16, height: 16, bgcolor: 'black', borderRadius: 1, border: '1px solid #ccc' }} />
                  Black
                </Box>
              </ToggleButton>
              <ToggleButton value="white" aria-label="white">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 16, height: 16, bgcolor: 'white', borderRadius: 1, border: '1px solid #ccc' }} />
                  White
                </Box>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}
        
        {/* Power Wire Gauge and Audio Wire Type dropdowns hidden for hybrid cables */}
        {/* Power wire gauge is always 14AWG for hybrid cables */}
        {/* Audio wire type is automatically determined for hybrid cables */}
      </Box>

      {/* Length fields ABOVE SVG */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {lengthLabels.map(({ id, label }) => {
          let val = id === 'A' ? overallLen : (lengths[id] || '');
          let displayVal = val;
          if (unit === 'ft') {
            displayVal = (val / 12).toFixed(2).replace(/\.00$/, '');
          }
          const edited = isEdited(id);
          if (id === 'A') {
            return (
              <Grid item xs={12} sm={6} md={2} key={id}>
                <TextField
                  label="A (Overall Length)"
                  value={overallInput}
                  onFocus={() => handleLengthFocus(id)}
                  onChange={e => handleOverallInputChange(e.target.value)}
                  onBlur={commitOverallInput}
                  onKeyDown={e => { if (e.key === 'Enter') commitOverallInput(); }}
                  type="number"
                  inputProps={{ min: 0, step: unit === 'ft' ? 1/12 : 1 }}
                  fullWidth
                  InputProps={{
                    endAdornment: <span style={{ display: 'flex', alignItems: 'center' }}>{unit === 'ft' ? <span style={{ marginLeft: 4 }}>'</span> : <span style={{ marginLeft: 4 }}>&quot;</span>}{edited && <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 18, ml: 0.5 }} titleAccess="User set" />}</span>
                  }}
                  helperText={unit === 'ft' ? "Max: 500 feet" : "Max: 6000 inches"}
                />
              </Grid>
            );
          }
          return (
            <Grid item xs={12} sm={6} md={2} key={id}>
              <TextField
                label={label}
                value={displayVal}
                onFocus={() => handleLengthFocus(id)}
                onChange={e => handleLengthChange(id, e.target.value)}
                type="number"
                inputProps={{ 
                  min: 0, 
                  step: unit === 'ft' ? 1/12 : 1,
                  readOnly: id === 'B' // Make B read-only since it's calculated
                }}
                fullWidth
                disabled={id === 'B'}
                sx={{
                  '& .MuiInputBase-input.Mui-disabled': {
                    WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)',
                    backgroundColor: '#f5f5f5',
                  }
                }}
                InputProps={{
                  endAdornment: <span style={{ display: 'flex', alignItems: 'center' }}>{unit === 'ft' ? <span style={{ marginLeft: 4 }}>'</span> : <span style={{ marginLeft: 4 }}>&quot;</span>}{id === 'B' ? <Tooltip title="Calculated automatically"><InfoIcon color="action" sx={{ fontSize: 18, ml: 0.5 }} /></Tooltip> : edited && <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 18, ml: 0.5 }} titleAccess="User set" />}</span>
                }}
                helperText={id === 'B' ? undefined : (unit === 'ft' ? "Max: 500 feet" : "Max: 6000 inches")}
              />
            </Grid>
          );
        })}
      </Grid>

      {/* Interactive SVG Diagram */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        mb: 2,
        overflow: 'hidden',
        width: '100%',
        maxWidth: '100%',
        border: '1px solid #ddd',
        borderRadius: 2,
        cursor: isPanning ? 'grabbing' : 'grab',
        '& svg': {
          maxWidth: '100%',
          height: 'auto'
        }
      }}>
        <svg 
          ref={actualSvgRef}
          id="hybrid-connector-selection-svg"
          width="800" 
          height="400"
          viewBox={`${viewBoxX} ${viewBoxY} ${viewBoxWidth} ${viewBoxHeight}`} 
          preserveAspectRatio="xMidYMid meet"
          xmlns="http://www.w3.org/2000/svg"
          xmlnsXlink="http://www.w3.org/1999/xlink"
          onMouseDown={handleSvgMouseDown}
          onMouseMove={handleSvgMouseMove}
          onMouseUp={handleSvgMouseUp}
          onMouseLeave={handleSvgMouseUp}
          onTouchStart={handleSvgTouchStart}
          onTouchMove={handleSvgTouchMove}
          onTouchEnd={handleSvgTouchEnd}
          style={{ 
            touchAction: 'none',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none'
          }}
        >
          <defs>
            <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="2" dy="3" stdDeviation="3" floodColor="#000" floodOpacity="0.3" />
            </filter>
          </defs>
          
          <g 
            style={groupStyle}
            transform={`translate(${svgTransform.x}, ${svgTransform.y}) scale(${svgTransform.scale})`}
          >
            {/* Central wire (A) - Represents the main cable length */}
            <path 
              d={generateStraightWire(200, 750, 251, parseFloat(lengths['A']) || 6)} 
              stroke={cableColor === 'white' ? '#e0e0e0' : '#666'} 
              strokeWidth="8" 
              fill="none" 
              filter="url(#shadow)"
            />
            
            {/* Cable sheath outline for better visibility */}
            <rect x="200" y="245" width="550" height="12" rx="6" fill={cableColor === 'white' ? '#f5f5f5' : '#000'} stroke={cableColor === 'white' ? '#d0d0d0' : '#b32b2b'} strokeWidth=".5" opacity="0.7" />
            
            {/* Audio branches (G, H) - Lighter blue, thin with prettier curves */}
            <path d={`M 200 251 C ${200 - 30} 230 ${200 - cVisual * 0.7} 190 ${200 - cVisual - 10} 160 C ${200 - cVisual - 25} 145 ${G_end.x + 15} ${G_end.y + 15} ${G_end.x} ${G_end.y}`} stroke="#6495c8" strokeWidth="6" fill="none" filter="url(#shadow)" strokeLinecap="round" />
            <path d={`M 750 251 C ${750 + 30} 230 ${750 + dVisual * 0.7} 190 ${750 + dVisual + 10} 160 C ${750 + dVisual + 25} 145 ${H_end.x - 15} ${H_end.y + 15} ${H_end.x} ${H_end.y}`} stroke="#6495c8" strokeWidth="6" fill="none" filter="url(#shadow)" strokeLinecap="round" />
            
            {/* Power branches (I, J) - Black, same thickness as audio with prettier curves */}
            <path d={`M 200 251 C ${200 - 30} 270 ${200 - eVisual * 0.7} 310 ${200 - eVisual - 10} 340 C ${200 - eVisual - 25} 355 ${I_end.x + 15} ${I_end.y - 15} ${I_end.x} ${I_end.y}`} stroke={cableColor === 'white' ? '#c0c0c0' : '#333333'} strokeWidth="6" fill="none" filter="url(#shadow)" strokeLinecap="round" />
            <path d={`M 750 251 C ${750 + 30} 270 ${750 + fVisual * 0.7} 310 ${750 + fVisual + 10} 340 C ${750 + fVisual + 25} 355 ${J_end.x - 15} ${J_end.y - 15} ${J_end.x} ${J_end.y}`} stroke={cableColor === 'white' ? '#c0c0c0' : '#333333'} strokeWidth="6" fill="none" filter="url(#shadow)" strokeLinecap="round" />

            {/* Connector points */}
            {connectorPoints.map(point => {
              const endPos = point.id === 'G' ? G_end :
                            point.id === 'H' ? H_end :
                            point.id === 'I' ? I_end :
                            J_end;
              const color = (point.type === 'audio' || point.type === 'hybrid') ? '#6495c8' : '#333333';
              const connector = connectors[point.id];
              
              return (
                <g key={point.id} onClick={() => handlePointClick(point.id)} style={{ cursor: 'pointer' }}>
                  {connector?.image ? (
                    <image 
                      href={getImageUrlForDisplay(connectorImages[point.id] || connector.image)}
                      x={endPos.x - connW / 2} 
                      y={endPos.y - connH / 2} 
                      width={connW} 
                      height={connH} 
                      filter="url(#shadow)"
                    />
                  ) : (
                    <rect 
                      x={endPos.x - connW / 2} 
                      y={endPos.y - connH / 2} 
                      width={connW} 
                      height={connH} 
                      rx="6" 
                      fill={color} 
                      stroke="#fff" 
                      strokeWidth="3" 
                      filter="url(#shadow)"
                    />
                  )}
                  <text 
                    x={endPos.x} 
                    y={endPos.y + connH / 2 + 20} 
                    textAnchor="middle" 
                    fontSize="14" 
                    fill="#000" 
                    fontWeight="bold"
                  >
                    {point.label}
                  </text>
                </g>
              );
            })}

            {/* Overall length line and label */}
            <line x1={leftmostX} y1={overallLineY} x2={rightmostX} y2={overallLineY} stroke="#bbb" strokeWidth="2" />
            <line x1={leftmostX} y1={overallLineY-5} x2={leftmostX} y2={overallLineY+5} stroke="#bbb" strokeWidth="2" />
            <line x1={rightmostX} y1={overallLineY-5} x2={rightmostX} y2={overallLineY+5} stroke="#bbb" strokeWidth="2" />
            <rect x={(leftmostX + rightmostX) / 2 - 48} y={overallLineY - 25} width="96" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
            <text x={(leftmostX + rightmostX) / 2} y={overallLineY - 15} textAnchor="middle" fontSize="16" fill="#666" fontWeight="500" dominantBaseline="middle">
              {unit === 'ft' ? `A = ${(parseFloat(overallLen) / 12).toFixed(2).replace(/\.00$/, '')}'` : `A = ${overallLen}"`}
            </text>

            {/* B length trunk line - positioned on the middle wire */}
            <rect x={(200 + 750) / 2 - 48} y={251 - 10} width="96" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
            <text x={(200 + 750) / 2} y={251 + 5} textAnchor="middle" fontSize="16" fill="#666" fontWeight="500">
              {unit === 'ft' ? `B = ${(parseFloat(lengths['B']) / 12).toFixed(2).replace(/\.00$/, '')}'` : `B = ${parseFloat(lengths['B']) || 0}"`}
            </text>
            
            {/* Length labels with oval outlined boxes - no dimension lines */}
            <rect x={cLabel.x-32} y={cLabel.y-10} width="64" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
            <text x={cLabel.x} y={cLabel.y+5} textAnchor="middle" fontSize="16" fill="#666" fontWeight="500">
              {lengths['C'] ? (unit === 'ft' ? `C = ${(parseFloat(lengths['C']) / 12).toFixed(2).replace(/\.00$/, '')}'` : `C = ${lengths['C']}"`) : 'C'}
            </text>
            <rect x={dLabel.x-32} y={dLabel.y-10} width="64" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
            <text x={dLabel.x} y={dLabel.y+5} textAnchor="middle" fontSize="16" fill="#666" fontWeight="500">
              {lengths['D'] ? (unit === 'ft' ? `D = ${(parseFloat(lengths['D']) / 12).toFixed(2).replace(/\.00$/, '')}'` : `D = ${lengths['D']}"`) : 'D'}
            </text>
            <rect x={eLabel.x-32} y={eLabel.y-10} width="64" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
            <text x={eLabel.x} y={eLabel.y+5} textAnchor="middle" fontSize="16" fill="#666" fontWeight="500">
              {lengths['E'] ? (unit === 'ft' ? `E = ${(parseFloat(lengths['E']) / 12).toFixed(2).replace(/\.00$/, '')}'` : `E = ${lengths['E']}"`) : 'E'}
            </text>
            <rect x={fLabel.x-32} y={fLabel.y-10} width="64" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
            <text x={fLabel.x} y={fLabel.y+5} textAnchor="middle" fontSize="16" fill="#666" fontWeight="500">
              {lengths['F'] ? (unit === 'ft' ? `F = ${(parseFloat(lengths['F']) / 12).toFixed(2).replace(/\.00$/, '')}'` : `F = ${lengths['F']}"`) : 'F'}
            </text>
          </g>
        </svg>
      </Box>

      {/* Connector summary row BELOW SVG with configuration options */}
      <Grid container spacing={2} sx={{ mb: 2, alignItems: 'center' }}>
        {connectorPoints.map((point) => {
          const connector = connectors[point.id];
          const isSelected = connector?.type;
          const currentConfig = connector?.configuration || 'standard';
          let connectorLabel = 'Not selected';
          if (connector?.id) {
            // Get the proper description from the compatibility matrix
            const matrixEntry = compatibilityMatrix[connector.id];
            if (matrixEntry?.description) {
              connectorLabel = matrixEntry.description;
            } else if (connector.description) {
              connectorLabel = connector.description;
            } else if (connector.label) {
              connectorLabel = connector.label;
            } else {
              connectorLabel = connector.id;
            }
          }
          
          return (
            <Grid item xs={6} md={3} key={point.id}>
              <Paper variant="outlined" sx={{ 
                p: 1, 
                cursor: 'pointer', 
                minHeight: 120, 
                display: 'flex', 
                flexDirection: 'column', 
                height: '100%', 
                position: 'relative',
                ...(iec12AWGError && isIECConnector(connector) && {
                  border: '2px solid #d32f2f',
                  backgroundColor: '#ffebee'
                })
              }} onClick={() => handlePointClick(point.id)}>
                {/* Clear button - positioned absolutely in top-right corner */}
                {connector?.id && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClearConnector(point.id);
                    }}
                    sx={{ 
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      color: '#d32f2f',
                      backgroundColor: 'rgba(211, 47, 47, 0.1)',
                      '&:hover': {
                        backgroundColor: 'rgba(211, 47, 47, 0.2)',
                        color: '#b71c1c'
                      },
                      width: 24,
                      height: 24,
                      zIndex: 1
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
                <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                    {/* Color-coded box for cable type */}
                    <Box sx={{
                      width: 16,
                      height: 16,
                      backgroundColor: (point.type === 'audio' || point.type === 'hybrid') ? '#6495c8' : '#333333',
                      border: '1px solid #fff',
                      borderRadius: '2px',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
                    }} />
                    {point.type === 'audio' && <><span role="img" aria-label="audio" style={{ fontSize: '16px' }}>🎵</span></>}
                    {point.type === 'power' && <><span role="img" aria-label="power" style={{ fontSize: '16px' }}>⚡</span></>}
                    {connector?.image ? (
                      <img src={getConnectorImage(connector, currentConfig)} alt={connector.label} style={{ width: '40px', height: '40px', objectFit: 'contain' }} />
                    ) : (
                      <Box sx={{ width: 40, height: 40, border: '2px dashed #ccc', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', color: '#999' }}>?</Box>
                    )}
                    {connector?.type && !(iec12AWGError && isIECConnector(connector)) && <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 18, ml: 0.5 }} titleAccess="Selected" />}
                    {iec12AWGError && isIECConnector(connector) && <WarningIcon sx={{ color: '#d32f2f', fontSize: 18, ml: 0.5 }} titleAccess="IEC connector incompatible with 12 AWG" />}
                  </Box>
                  <Typography variant="subtitle2" color="text.secondary" align="center">Connector {point.label}</Typography>
                  <Typography variant="body2" align="center" sx={{ mt: 0.5 }}>
                    {connectorLabel}
                  </Typography>
                </Box>
                
                {/* Configuration Selector - show when connector is selected */}
                {isSelected && (
                  <Box sx={{ mt: 'auto', pt: 1, borderTop: '1px solid', borderColor: 'divider', width: '100%' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', fontSize: '0.7rem', textAlign: 'center' }}>
                      Configuration:
                    </Typography>
                    <ToggleButtonGroup
                      value={currentConfig}
                      exclusive
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e, newConfig) => {
                        e.stopPropagation();
                        if (newConfig !== null) {
                          handleConfigurationChange(point.id, newConfig, e);
                        }
                      }}
                      aria-label={`configuration for connector ${point.label}`}
                      size="small"
                      sx={{ 
                        width: '100%',
                        '& .MuiToggleButton-root': {
                          fontSize: '0.65rem',
                          px: 0.25,
                          py: 0.2,
                          minWidth: 'unset',
                          flex: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          '&.Mui-selected': {
                            backgroundColor: '#e8f5e8',
                            color: '#2e7d32',
                            border: '1px solid #4caf50',
                            '&:hover': {
                              backgroundColor: '#dcedc8'
                            }
                          }
                        }
                      }}
                    >
                      {(() => {
                        const standardCompat = checkConfigurationCompatibility(point.id, 'standard');
                        const rightAngleCompat = checkConfigurationCompatibility(point.id, 'right-angle');
                        const panelMountCompat = checkConfigurationCompatibility(point.id, 'panel-mount');
                        
                        return (
                          <>
                            <Tooltip 
                              title={
                                !getConfigurationsForConnector(connector).standard
                                  ? "Configuration not available for this connector type"
                                  : standardCompat?.incompatible 
                                    ? `Incompatible: ${standardCompat.reason}` 
                                    : "Standard straight connector"
                              }
                              arrow
                            >
                              <span style={{ flex: 1 }}>
                                <ToggleButton 
                                  value="standard" 
                                  aria-label="standard" 
                                  disabled={!getConfigurationsForConnector(connector).standard}
                                  sx={{ 
                                    flex: 1, 
                                    minWidth: 0, 
                                    px: 0.5,
                                    width: '100%',
                                    border: checkConfigurationCompatibility(point.id, 'standard')?.incompatible ? '2px solid #f44336' : '1px solid',
                                    borderColor: checkConfigurationCompatibility(point.id, 'standard')?.incompatible ? '#f44336' : 'divider',
                                    ...(standardCompat?.incompatible && {
                                      backgroundColor: '#ffebee',
                                      color: '#c62828',
                                      '&:hover': {
                                        backgroundColor: '#ffcdd2'
                                      }
                                    }),
                                    '&.Mui-selected': {
                                      backgroundColor: '#e8f5e8',
                                      color: '#2e7d32',
                                      border: checkConfigurationCompatibility(point.id, 'standard')?.incompatible ? '2px solid #f44336' : '1px solid #4caf50',
                                      '&:hover': {
                                        backgroundColor: '#dcedc8'
                                      }
                                    },
                                    '&.Mui-disabled': {
                                      color: 'rgba(0, 0, 0, 0.26)',
                                      backgroundColor: 'rgba(0, 0, 0, 0.12)'
                                    }
                                  }}
                                >
                                  STD
                                </ToggleButton>
                              </span>
                            </Tooltip>
                            <Tooltip 
                              title={
                                !getConfigurationsForConnector(connector)['right-angle']
                                  ? "Configuration not available for this connector type"
                                  : rightAngleCompat?.incompatible 
                                    ? `Incompatible: ${rightAngleCompat.reason}` 
                                    : "Right Angle (90°) connector"
                              }
                              arrow
                            >
                              <span style={{ flex: 1 }}>
                                <ToggleButton 
                                  value="right-angle" 
                                  aria-label="right angle" 
                                  disabled={!getConfigurationsForConnector(connector)['right-angle']}
                                  sx={{ 
                                    flex: 1, 
                                    minWidth: 0, 
                                    px: 0.5,
                                    width: '100%',
                                    border: checkConfigurationCompatibility(point.id, 'right-angle')?.incompatible ? '2px solid #f44336' : '1px solid',
                                    borderColor: checkConfigurationCompatibility(point.id, 'right-angle')?.incompatible ? '#f44336' : 'divider',
                                    ...(rightAngleCompat?.incompatible && {
                                      backgroundColor: '#ffebee',
                                      color: '#c62828',
                                      '&:hover': {
                                        backgroundColor: '#ffcdd2'
                                      }
                                    }),
                                    '&.Mui-selected': {
                                      backgroundColor: '#e8f5e8',
                                      color: '#2e7d32',
                                      border: checkConfigurationCompatibility(point.id, 'right-angle')?.incompatible ? '2px solid #f44336' : '1px solid #4caf50',
                                      '&:hover': {
                                        backgroundColor: '#dcedc8'
                                      }
                                    },
                                    '&.Mui-disabled': {
                                      color: 'rgba(0, 0, 0, 0.26)',
                                      backgroundColor: 'rgba(0, 0, 0, 0.12)'
                                    }
                                  }}
                                >
                                  RA
                                </ToggleButton>
                              </span>
                            </Tooltip>
                            <Tooltip 
                              title={
                                !getConfigurationsForConnector(connector)['panel-mount']
                                  ? "Configuration not available for this connector type"
                                  : panelMountCompat?.incompatible 
                                    ? `Incompatible: ${panelMountCompat.reason}` 
                                    : "Panel mount connector"
                              }
                              arrow
                            >
                              <span style={{ flex: 1 }}>
                                <ToggleButton 
                                  value="panel-mount" 
                                  aria-label="panel mount" 
                                  disabled={!getConfigurationsForConnector(connector)['panel-mount']}
                                  sx={{ 
                                    flex: 1, 
                                    minWidth: 0, 
                                    px: 0.5,
                                    width: '100%',
                                    border: checkConfigurationCompatibility(point.id, 'panel-mount')?.incompatible ? '2px solid #f44336' : '1px solid',
                                    borderColor: checkConfigurationCompatibility(point.id, 'panel-mount')?.incompatible ? '#f44336' : 'divider',
                                    ...(panelMountCompat?.incompatible && {
                                      backgroundColor: '#ffebee',
                                      color: '#c62828',
                                      '&:hover': {
                                        backgroundColor: '#ffcdd2'
                                      }
                                    }),
                                    '&.Mui-selected': {
                                      backgroundColor: '#e8f5e8',
                                      color: '#2e7d32',
                                      border: checkConfigurationCompatibility(point.id, 'panel-mount')?.incompatible ? '2px solid #f44336' : '1px solid #4caf50',
                                      '&:hover': {
                                        backgroundColor: '#dcedc8'
                                      }
                                    },
                                    '&.Mui-disabled': {
                                      color: 'rgba(0, 0, 0, 0.26)',
                                      backgroundColor: 'rgba(0, 0, 0, 0.12)'
                                    }
                                  }}
                                >
                                  PNL
                                </ToggleButton>
                              </span>
                            </Tooltip>
                          </>
                        );
                      })()}
                    </ToggleButtonGroup>
                  </Box>
                )}
                {connector && !compatibilityResults[connector.id]?.compatible && (
                  <CompatibilityWarning compatibilityResult={compatibilityResults[connector.id]} connectorA={connector} connectorB={null} />
                )}
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      {/* Special Instructions */}
      <TextField
        label="Special Instructions (Optional)"
        multiline
        rows={3}
        fullWidth
        value={specialInstructions}
        onChange={handleInstructionsChange}
        sx={{ mb: 2 }}
        placeholder="How are you going to use this cable? We evaluate every cable we build... Please DO NOT include any personal information!"
      />

      {/* Enhanced Compatibility Warnings */}
      {Object.keys(compatibilityWarnings).length > 0 && (
        <Box sx={{ mb: 2 }}>
          {Object.entries(compatibilityWarnings).map(([key, warning]) => (
            <Alert 
              key={key}
              severity={warning.type || 'warning'} 
              sx={{ mb: 1 }}
              icon={<WarningIcon />}
            >
              <Typography variant="body2" fontWeight="bold">
                Connector Compatibility Note: {key.replace('-', ' ↔ ')}
              </Typography>
              <Typography variant="body2">
                {warning.message}
              </Typography>
              {warning.details && (
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                  {warning.details}
                </Typography>
              )}
            </Alert>
          ))}
        </Box>
      )}

      <Dialog
        open={modalOpen}
        onClose={handleModalClose}
        aria-labelledby="connector-modal-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="connector-modal-title">
          Select {connectorPoints.find(p => p.id === selectedPoint)?.type === 'power' ? 'Power' : 'Hybrid'} Connector for Point {selectedPoint}
        </DialogTitle>
        <DialogContent>
          <ConnectorModal 
            key={`modal-${selectedPoint}`}
            selectedPoint={selectedPoint} 
            onConnectorSelect={handleConnectorSelect} 
            filteredConnectors={filteredConnectors} 
            type={type}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleModalClose}>Cancel</Button>
        </DialogActions>
      </Dialog>
      
      {/* Tooltip for configuration change notifications */}
      <Tooltip
        open={tooltipOpen}
        anchorEl={tooltipAnchor}
        onClose={() => setTooltipOpen(false)}
        title={tooltipMessage}
        arrow
        placement="top"
        sx={{
          '& .MuiTooltip-tooltip': {
            backgroundColor: '#d32f2f',
            color: 'white',
            fontSize: '0.875rem',
            maxWidth: 300
          }
        }}
      />
    </Box>
  );
}

export default HybridConnectorSelection; 